import type { CadWorkbenchModeId } from '../config/modelingCommandRegistry';
import type { DeliverableQualityRecord } from '../types/deliverableQuality';
import type {
  DocumentEditorState,
  DocumentSaveRequest,
  DocumentSaveResponse,
  DocumentSession,
} from '../features/document-editor/types';
import type {
  PidDiagram,
  PidDiagramDefinition,
  PidSaveRequest,
  PidSaveResponse,
  PidValidationResponse,
} from '../types/pid';
import type {
  SingleLineCalculationResponse,
  SingleLineDiagram,
  SingleLineDiagramDefinition,
  SingleLineDiagramSummary,
  SingleLineRevision,
  SingleLineRevisionPage,
  SingleLineSaveResponse,
  SingleLineValidationResponse,
} from '../types/singleLine';
import type { SpreadsheetPivotDefinition } from '../features/spreadsheet/calc/types';
import { getTenantHeaders } from '../utils/tenantApiHeaders';

const getApiUrl = () => {
  // Check if we have a configured API URL
  if (import.meta.env.VITE_API_URL) {
    return import.meta.env.VITE_API_URL;
  }

  // Both production and development use /api prefix
  // In production: FastAPI serves API routes with /api prefix
  // In development: Vite proxy handles /api prefix
  return '/api';
};

const API_BASE_URL = getApiUrl();

const getRequestAuthHeaders = (): Record<string, string> => {
  const token = localStorage.getItem('authToken');
  const auth = token ? { Authorization: `Bearer ${token}` } : {};
  return { ...auth, ...getTenantHeaders() };
};

export interface ArtifactType {
  id: string;
  type: 'PDF' | 'SVG' | 'CSV' | 'TXT' | 'MARKDOWN' | 'JSON' | 'EXCEL' | 'IMAGE';
  url: string;
  meta_data?: {
    display_name?: string;
    document_type?: string;
    [key: string]: unknown;
  };
  created_at: string;
}

export interface ModelContextWindow {
  model_name: string;
  context_window: number;
  tool_token_reserve: number;
  response_token_reserve: number;
  created_at: string;
}

export class ApiError<TDetail = unknown> extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly detail: TDetail
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const apiErrorMessage = (payload: unknown, status: number): string => {
  if (!isRecord(payload)) return `HTTP error! status: ${status}`;
  if (typeof payload.message === 'string') return payload.message;

  const detail = payload.detail;
  if (typeof detail === 'string') return detail;
  if (isRecord(detail) && typeof detail.message === 'string') {
    return detail.message;
  }
  if (Array.isArray(detail)) {
    const messages = detail
      .map((item) =>
        isRecord(item) && typeof item.msg === 'string' ? item.msg : null
      )
      .filter((message): message is string => Boolean(message));
    if (messages.length) return messages.join(', ');
  }
  return `HTTP error! status: ${status}`;
};

const apiErrorFromResponse = async (response: Response): Promise<ApiError> => {
  const payload: unknown = await response
    .json()
    .catch(() => ({ message: 'Unknown error' }));
  const detail =
    isRecord(payload) && 'detail' in payload ? payload.detail : payload;
  return new ApiError(
    response.status,
    apiErrorMessage(payload, response.status),
    detail
  );
};

class ApiClient {
  private baseURL: string;

  constructor(baseURL: string) {
    this.baseURL = baseURL;
  }

  private getAuthHeaders(): Record<string, string> {
    return getRequestAuthHeaders();
  }

  private async handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
      throw await apiErrorFromResponse(response);
    }

    if (response.status === 204) {
      return undefined as T;
    }

    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      return response.json();
    }

    return response.text() as unknown as T;
  }

  async get<T>(endpoint: string): Promise<T> {
    const response = await fetch(`${this.baseURL}${endpoint}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...this.getAuthHeaders(),
      },
    });

    return this.handleResponse<T>(response);
  }

  async post<T>(endpoint: string, data?: unknown): Promise<T> {
    const response = await fetch(`${this.baseURL}${endpoint}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...this.getAuthHeaders(),
      },
      body: data ? JSON.stringify(data) : undefined,
    });

    return this.handleResponse<T>(response);
  }

  async uploadFile<T>(endpoint: string, formData: FormData): Promise<T> {
    const response = await fetch(`${this.baseURL}${endpoint}`, {
      method: 'POST',
      headers: {
        ...this.getAuthHeaders(),
      },
      body: formData,
    });

    return this.handleResponse<T>(response);
  }

  async put<T>(endpoint: string, data?: unknown): Promise<T> {
    const response = await fetch(`${this.baseURL}${endpoint}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...this.getAuthHeaders(),
      },
      body: data ? JSON.stringify(data) : undefined,
    });

    return this.handleResponse<T>(response);
  }

  async patch<T>(endpoint: string, data?: unknown): Promise<T> {
    const response = await fetch(`${this.baseURL}${endpoint}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        ...this.getAuthHeaders(),
      },
      body: data ? JSON.stringify(data) : undefined,
    });

    return this.handleResponse<T>(response);
  }

  async delete<T>(endpoint: string): Promise<T> {
    const response = await fetch(`${this.baseURL}${endpoint}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        ...this.getAuthHeaders(),
      },
    });

    return this.handleResponse<T>(response);
  }

  /**
   * Download a file as a Blob
   */
  async downloadBlob(endpoint: string): Promise<Blob> {
    const response = await fetch(`${this.baseURL}${endpoint}`, {
      method: 'GET',
      headers: {
        ...this.getAuthHeaders(),
      },
    });

    if (!response.ok) {
      throw await apiErrorFromResponse(response);
    }

    return response.blob();
  }

  /**
   * GET request returning raw Response for streaming
   */
  async getStream(endpoint: string): Promise<Response> {
    const response = await fetch(`${this.baseURL}${endpoint}`, {
      method: 'GET',
      headers: {
        ...this.getAuthHeaders(),
      },
    });

    if (!response.ok) {
      throw await apiErrorFromResponse(response);
    }

    return response;
  }

  /**
   * POST request returning raw Response for streaming
   */
  async postStream(endpoint: string, data?: unknown): Promise<Response> {
    const response = await fetch(`${this.baseURL}${endpoint}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...this.getAuthHeaders(),
      },
      body: data ? JSON.stringify(data) : undefined,
    });

    if (!response.ok) {
      throw await apiErrorFromResponse(response);
    }

    return response;
  }

  /**
   * Get the base URL for this client
   */
  getBaseUrl(): string {
    return this.baseURL;
  }
}

const apiClient = new ApiClient(API_BASE_URL);

const fetchBlobResponse = async (endpoint: string): Promise<Blob> => {
  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    method: 'GET',
    headers: getRequestAuthHeaders(),
  });
  if (!response.ok) {
    throw await apiErrorFromResponse(response);
  }
  return response.blob();
};

// Types
interface Project {
  id: string;
  name: string;
  status: string;
  created_at: string;
  updated_at: string;
  created_by_email?: string;
  created_by_name?: string;
}

export interface PortfolioProject {
  id: string;
  name: string;
  status: string;
  created_at: string;
  updated_at: string;
  created_by_email?: string;
  created_by_name?: string;
}

export type PMHealth = 'not_started' | 'on_track' | 'at_risk' | 'complete';
export type PMItemKind = 'PHASE' | 'TASK' | 'MILESTONE' | 'BUFFER';
export type PMSuggestionStatus = 'STAGED' | 'APPLIED' | 'REJECTED';

export interface PMScheduleItem {
  id: string;
  name: string;
  kind: PMItemKind;
  order?: number | null;
  duration_days?: number | null;
  start_date?: string | null;
  end_date?: string | null;
  percent_complete?: number | null;
  dependencies?: string[];
  owner?: string | null;
  notes?: string | null;
  metadata?: Record<string, unknown> | null;
}

export interface PMProjectScheduleData {
  start_date?: string | null;
  target_end_date?: string | null;
  items: PMScheduleItem[];
  notes?: string | null;
  metadata?: Record<string, unknown>;
}

export interface PMProjectScheduleSummary {
  item_count: number;
  milestone_count: number;
  planned_start_date?: string | null;
  planned_end_date?: string | null;
  total_duration_days: number;
  progress_percent: number;
  late_item_count: number;
  health: PMHealth;
  suggestion_count: number;
}

export interface PMScheduleSuggestion {
  id: string;
  project_id: string;
  schedule_id: string;
  suggestion_type: string;
  title: string;
  description?: string | null;
  status: PMSuggestionStatus;
  suggestion: Record<string, unknown>;
  sort_order: number;
  created_at: string;
  updated_at: string;
  applied_at?: string | null;
  rejected_at?: string | null;
}

export interface PMProjectScheduleResponse {
  project_id: string;
  project_name?: string | null;
  schedule_id?: string | null;
  company_id?: string | null;
  version: number;
  schedule: PMProjectScheduleData;
  summary: PMProjectScheduleSummary;
  suggestions: PMScheduleSuggestion[];
  recalculated_at?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface PMPortfolioProjectSummary {
  project_id: string;
  project_name: string;
  project_status: string;
  company_id?: string | null;
  has_schedule: boolean;
  schedule_version?: number | null;
  item_count: number;
  planned_start_date?: string | null;
  planned_end_date?: string | null;
  progress_percent: number;
  late_item_count: number;
  health: PMHealth;
  suggestion_count: number;
  recalculated_at?: string | null;
}

export interface PMPortfolioRollup {
  portfolio: {
    project_count: number;
    scheduled_project_count: number;
    active_project_count: number;
    completed_project_count: number;
    at_risk_project_count: number;
    overall_progress_percent: number;
    planned_start_date?: string | null;
    planned_end_date?: string | null;
  };
  projects: PMPortfolioProjectSummary[];
}

export interface Artifact {
  id: string;
  task_id?: string | null;
  type: string;
  filename: string;
  file_size?: number;
  created_at: string;
  metadata?: {
    display_name?: string;
    description?: string;
  };
  source?: 'uploaded' | 'generated';
}

interface ProjectDashboard {
  project: Project;
  tasks: Array<{
    id: string;
    name: string;
    status: string;
    created_at: string;
    queued_at?: string | null;
    started_at?: string | null;
    dependencies?: string[];
  }>;
  artifacts: Artifact[];
}

// API Functions
export const getProjects = (): Promise<Project[]> => {
  return apiClient.get<Project[]>('/projects/list');
};

export const listPortfolioProjects = (): Promise<PortfolioProject[]> => {
  return apiClient.get<PortfolioProject[]>('/projects/list');
};

export const getPMPortfolio = (): Promise<PMPortfolioRollup> => {
  return apiClient.get<PMPortfolioRollup>('/pm/portfolio');
};

export const getPMProjectSchedule = (
  projectId: string
): Promise<PMProjectScheduleResponse> => {
  return apiClient.get<PMProjectScheduleResponse>(
    `/pm/projects/${projectId}/schedule`
  );
};

export const savePMProjectSchedule = (
  projectId: string,
  payload: Partial<PMProjectScheduleData>
): Promise<PMProjectScheduleResponse> => {
  return apiClient.put<PMProjectScheduleResponse>(
    `/pm/projects/${projectId}/schedule`,
    payload
  );
};

export const recalculatePMProjectSchedule = (
  projectId: string,
  payload: { start_date?: string | null } = {}
): Promise<PMProjectScheduleResponse> => {
  return apiClient.post<PMProjectScheduleResponse>(
    `/pm/projects/${projectId}/schedule/recalculate`,
    payload
  );
};

export const generatePMScheduleSuggestions = (
  projectId: string,
  payload: { limit?: number; refresh_existing?: boolean } = {}
): Promise<PMProjectScheduleResponse> => {
  return apiClient.post<PMProjectScheduleResponse>(
    `/pm/projects/${projectId}/schedule/suggestions`,
    payload
  );
};

export const applyPMScheduleSuggestion = (
  projectId: string,
  suggestionId: string
): Promise<PMProjectScheduleResponse> => {
  return apiClient.post<PMProjectScheduleResponse>(
    `/pm/projects/${projectId}/schedule/suggestions/${suggestionId}/apply`,
    {}
  );
};

export const rejectPMScheduleSuggestion = (
  projectId: string,
  suggestionId: string
): Promise<PMScheduleSuggestion> => {
  return apiClient.post<PMScheduleSuggestion>(
    `/pm/projects/${projectId}/schedule/suggestions/${suggestionId}/reject`,
    {}
  );
};

export const getOrganizationProjects = (): Promise<Project[]> => {
  return apiClient.get<Project[]>('/projects/list/organization');
};

export const uploadProject = (params: {
  name: string;
  file?: File;
  files?: File[];
  selectedCompanyDocumentIds?: string[];
  selectedAnalyses: string[];
  analysisModels?: Record<string, string>;
  sections?: TemplateSectionInput[];
  templateId?: string;
}): Promise<unknown> => {
  const formData = new FormData();
  const hasSelectedCompanyDocs =
    !!params.selectedCompanyDocumentIds &&
    params.selectedCompanyDocumentIds.length > 0;
  if (params.files && params.files.length > 0) {
    for (const f of params.files) formData.append('files', f);
  } else if (params.file) {
    formData.append('file', params.file);
  } else if (!hasSelectedCompanyDocs) {
    throw new Error(
      'At least one source file or selected company document must be provided'
    );
  }
  if (hasSelectedCompanyDocs) {
    formData.append(
      'selected_company_document_ids',
      JSON.stringify(params.selectedCompanyDocumentIds)
    );
  }
  formData.append('name', params.name);
  formData.append('selected_analyses', JSON.stringify(params.selectedAnalyses));
  if (params.analysisModels) {
    formData.append('analysis_models', JSON.stringify(params.analysisModels));
  }
  // Send full sections with dependencies for proper task dependency resolution
  if (params.sections && params.sections.length > 0) {
    formData.append('sections', JSON.stringify(params.sections));
  }
  if (params.templateId) {
    formData.append('template_id', params.templateId);
  }

  return apiClient.uploadFile('/projects/upload', formData);
};

export const getProjectDashboard = (
  projectId: string
): Promise<ProjectDashboard> => {
  return apiClient.get<ProjectDashboard>(`/projects/${projectId}/dashboard`);
};

export interface Task {
  id: string;
  name: string;
  status: string;
  created_at: string;
  queued_at?: string | null;
  started_at?: string | null;
  dependencies?: string[];
}

export const getProjectTasks = (projectId: string): Promise<Task[]> => {
  return apiClient.get<Task[]>(`/projects/${projectId}/tasks`);
};

export const getTaskArtifacts = (taskId: string): Promise<Artifact[]> => {
  return apiClient.get<Artifact[]>(`/tasks/${taskId}/artifacts`);
};

export const cancelTask = (
  projectId: string,
  taskId: string
): Promise<{ message: string; status: string }> => {
  return apiClient.post<{ message: string; status: string }>(
    `/projects/${projectId}/tasks/${taskId}/cancel`
  );
};

// Note: getProjectInputArtifacts is defined below with proper InputArtifact type

// Notification stream (simplified polling for now)
export const getProjectNotifications = (
  projectId: string
): Promise<unknown> => {
  return apiClient.get(`/projects/notifications/${projectId}`);
};

export const deleteProject = (
  projectId: string
): Promise<{ message: string }> => {
  return apiClient.delete(`/projects/${projectId}`);
};

// Fine-tuning APIs
export interface CreateFineTuneRequest {
  company_id: string;
  base_model:
    | 'gpt-4.1-mini-2025-04-14'
    | 'gpt-4.1-nano-2025-04-14'
    | 'gpt-4o-mini-2024-07-18'
    | 'gpt-5-mini'
    | 'gpt-5-nano'
    | 'gpt-5.1'
    | 'gpt-5.2'
    | 'gpt-5.4'
    | 'gpt-5.4-mini'
    | 'gpt-5.4-nano'
    | 'gpt-5.5';
  include_doc_training?: boolean;
  include_20q?: boolean;
  user_answers?: Array<Record<string, unknown>>;
  display_name?: string;
  notes?: string;
  selected_artifact_ids?: string[];
  avoid_overfitting?: boolean;
  agent_type?: 'GENERALIST' | 'SPECIALIST';
}

export const createFineTune = (
  payload: CreateFineTuneRequest
): Promise<{
  fine_tuned_model_id: string;
  status: string;
  datasets: Record<string, string>;
}> => {
  return apiClient.post(`/fine-tunes`, payload);
};

export const activateFineTune = (
  companyId: string,
  fineTunedModelId: string
): Promise<{ ok: boolean }> => {
  return apiClient.post(`/fine-tunes/activate`, {
    company_id: companyId,
    fine_tuned_model_id: fineTunedModelId,
  });
};

/**
 * Set a model's active state (toggle on/off)
 * When activating, other models in the same lineage are auto-deactivated
 */
export const setModelActive = (
  modelId: string,
  isActive: boolean
): Promise<{ ok: boolean; is_active: boolean; model_id: string }> => {
  return apiClient.put(`/fine-tunes/${modelId}/active`, {
    is_active: isActive,
  });
};

/**
 * Fine-tune model with extended fields from API
 */
export interface FineTuneModel {
  id: string;
  base_model: string;
  openai_model_id?: string;
  status: string;
  display_name?: string;
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
  agent_type?: 'GENERALIST' | 'SPECIALIST';
  // Additional fields that may come from joined queries
  // NOTE: storage_uri excluded from list response for performance
  // (contains full dataset content). Fetch via /datasets endpoint on demand.
  datasets?: Array<{
    type?: string;
    item_count?: number;
    id?: string;
  }>;
  training_item_count?: number;
  training_dataset_id?: string;
  evaluation_base_overall?: number;
  evaluation_ft_overall?: number;
  evaluation_delta_overall?: number;
  win_rate_base?: number;
  win_rate_ft?: number;
  win_rate_ties?: number;
  eval_mcq_base?: number;
  eval_mcq_ft?: number;
  eval_mcq_delta?: number;
  training_method?: 'sft' | 'dpo';
  parent_model_id?: string | null;
}

export const listFineTunes = (
  companyId: string
): Promise<{ items: FineTuneModel[] }> => {
  return apiClient.get(`/fine-tunes?company_id=${companyId}`);
};

export const listCompanyArtifacts = (
  companyId: string
): Promise<{
  items: Array<{
    id: string;
    filename: string;
    type: string;
    project_id: string;
    created_at: string;
  }>;
}> => {
  return apiClient.get(`/companies/${companyId}/artifacts`);
};

export interface CompanyDocument {
  id: string;
  filename: string;
  content_type: string;
  file_size: number;
  status: string;
  version: number;
  meta_data?: Record<string, unknown>;
  created_at: string;
  linked_project_ids: string[];
}

export const listCompanyDocuments = (
  companyId: string,
  query?: string
): Promise<{ items: CompanyDocument[] }> => {
  const q = query ? `?q=${encodeURIComponent(query)}` : '';
  return apiClient.get(`/companies/${companyId}/documents${q}`);
};

export const uploadCompanyDocuments = async (
  companyId: string,
  files: File[],
  projectId?: string
): Promise<{ uploaded: string[]; project_input_artifact_ids: string[] }> => {
  const token = localStorage.getItem('authToken');
  const formData = new FormData();
  if (projectId) formData.append('project_id', projectId);
  for (const f of files) formData.append('files', f);
  const res = await fetch(
    `${API_BASE_URL}/companies/${companyId}/documents/upload`,
    {
      method: 'POST',
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: formData,
    }
  );
  if (!res.ok) throw new Error(`Upload failed: ${res.status}`);
  return res.json();
};

export const attachCompanyDocumentsToProject = (
  companyId: string,
  projectId: string,
  companyDocumentIds: string[]
): Promise<{
  attached_company_document_ids: string[];
  created_input_artifact_ids: string[];
}> => {
  return apiClient.post(`/companies/${companyId}/documents/attach`, {
    project_id: projectId,
    company_document_ids: companyDocumentIds,
  });
};

export type ExternalFileProvider = 'google_drive' | 'microsoft_365';

export interface ExternalFileIntegration {
  id: string;
  company_id: string;
  provider: ExternalFileProvider;
  display_name: string;
  provider_tenant_id?: string | null;
  provider_tenant_name?: string | null;
  status: string;
  last_sync_at?: string | null;
  last_error?: string | null;
  source_count: number;
  indexed_file_count: number;
}

export interface ExternalConnectorTermsStatus {
  accepted: boolean;
  terms_version: string;
  content: string;
  accepted_at?: string | null;
}

export interface ExternalFileSource {
  id: string;
  connector_id: string;
  source_type: string;
  provider_source_id: string;
  display_name: string;
  path?: string | null;
  status: string;
  sync_policy?: Record<string, unknown>;
  created_at?: string | null;
}

export interface ExternalFileSearchResult {
  id: string;
  connector_id: string;
  source_id?: string | null;
  provider_file_id: string;
  name: string;
  mime_type?: string | null;
  web_url?: string | null;
  path?: string | null;
  status: string;
  size?: number | null;
  modified_at?: string | null;
  indexing_status?: string | null;
  indexing_error?: string | null;
  classification_level?: string | null;
  sensitivity_label?: string | null;
  acl_snapshot_at?: string | null;
  content_extracted_at?: string | null;
  permission_summaries?: Array<{
    principal_type: string;
    principal_email?: string | null;
    principal_display_name?: string | null;
    role: string;
    inherited: boolean;
  }>;
  snippets?: Array<{
    chunk_id: string;
    order_index: number;
    text: string;
    section_pointer?: string | null;
    page_number?: number | null;
  }>;
}

export const listExternalFileIntegrations = (): Promise<{
  items: ExternalFileIntegration[];
}> => apiClient.get('/files/integrations');

export const connectExternalFileProvider = (
  provider: ExternalFileProvider,
  payload: {
    display_name: string;
    provider_tenant_id?: string;
    provider_tenant_name?: string;
    settings?: Record<string, unknown>;
  }
): Promise<{ integration: ExternalFileIntegration }> =>
  apiClient.post(`/files/integrations/${provider}/connect`, payload);

export const getExternalConnectorTerms = (
  provider: ExternalFileProvider
): Promise<ExternalConnectorTermsStatus> =>
  apiClient.get(`/files/integrations/${provider}/terms`);

export const acceptExternalConnectorTerms = (
  integrationId: string,
  payload?: {
    requested_scopes?: Record<string, unknown>;
    policy_snapshot?: Record<string, unknown>;
  }
): Promise<{
  accepted: boolean;
  terms_version: string;
  accepted_at?: string | null;
}> =>
  apiClient.post(
    `/files/integrations/${integrationId}/terms/accept`,
    payload || {}
  );

export const updateExternalFileIntegration = (
  integrationId: string,
  payload: {
    display_name?: string;
    status?: string;
    settings?: Record<string, unknown>;
  }
): Promise<{ integration: ExternalFileIntegration }> =>
  apiClient.patch(`/files/integrations/${integrationId}`, payload);

export const deleteExternalFileIntegration = (
  integrationId: string
): Promise<{ disabled: boolean }> =>
  apiClient.delete(`/files/integrations/${integrationId}`);

export const listExternalFileSources = (
  integrationId: string
): Promise<{ items: ExternalFileSource[] }> =>
  apiClient.get(`/files/integrations/${integrationId}/sources`);

export const addExternalFileSources = (
  integrationId: string,
  sources: Array<{
    source_type: string;
    provider_source_id: string;
    display_name?: string;
    path?: string;
    sync_policy?: Record<string, unknown>;
  }>
): Promise<{ items: ExternalFileSource[] }> =>
  apiClient.post(`/files/integrations/${integrationId}/sources`, { sources });

export const addExternalFileExclusion = (
  integrationId: string,
  payload: {
    rule_type: string;
    pattern: string;
    mode?: string;
    reason?: string;
  }
): Promise<{ rule: Record<string, unknown> }> =>
  apiClient.post(`/files/integrations/${integrationId}/exclusions`, payload);

export const purgeExternalFileIntegration = (
  integrationId: string
): Promise<{ purged_files: number }> =>
  apiClient.post(`/files/integrations/${integrationId}/purge`, {});

export const syncExternalFileIntegration = (
  integrationId: string,
  payload?: { source_id?: string; reason?: string; process_inline?: boolean }
): Promise<{
  job: {
    id: string;
    connector_id: string;
    source_id?: string | null;
    status: string;
    job_type: string;
    attempts: number;
  };
  result?: { job_id: string; indexed: number } | null;
}> =>
  apiClient.post(`/files/integrations/${integrationId}/sync`, payload || {});

export const getExternalFilePolicy = (): Promise<{
  policy: Record<string, unknown>;
  terms_version: string;
}> => apiClient.get('/files/policies');

export const updateExternalFilePolicy = (
  policy: Record<string, unknown>
): Promise<{ policy: Record<string, unknown> }> =>
  apiClient.put('/files/policies', { policy });

export const searchExternalFiles = (
  query?: string,
  projectId?: string,
  limit = 25
): Promise<{ items: ExternalFileSearchResult[] }> => {
  const params = new URLSearchParams();
  if (query) params.set('q', query);
  if (projectId) params.set('project_id', projectId);
  params.set('limit', String(limit));
  return apiClient.get(`/files/search?${params.toString()}`);
};

export const attachExternalSourcesToProject = (
  projectId: string,
  payload: { external_file_ids?: string[]; external_source_ids?: string[] }
): Promise<{
  attached_external_file_ids: string[];
  attached_external_source_ids: string[];
}> => apiClient.post(`/projects/${projectId}/external-sources`, payload);

export const listFineTuneEvaluations = (
  fineTunedModelId: string
): Promise<{
  items: Array<{
    id: string;
    metrics?: Record<string, unknown>;
    completed_at?: string;
    evaluated_model_id: string;
    base_model: string;
  }>;
}> => {
  return apiClient.get(`/fine-tunes/${fineTunedModelId}/evaluations`);
};

export const listFineTuneDatasets = (
  fineTunedModelId: string
): Promise<{
  items: Array<{
    id: string;
    type: string;
    storage_uri: string;
    created_at?: string;
    item_count?: number;
  }>;
}> => {
  return apiClient.get(`/fine-tunes/${fineTunedModelId}/datasets`);
};

export const deleteFineTune = (
  fineTunedModelId: string,
  confirmDisplayName: string
): Promise<{ ok: boolean }> => {
  return apiClient.post(`/fine-tunes/${fineTunedModelId}/delete`, {
    confirm_display_name: confirmDisplayName,
  });
};

export const resubmitFineTune = (
  fineTunedModelId: string,
  opts?: { base_model?: string; n_epochs?: number; avoid_overfitting?: boolean }
): Promise<{
  ok: boolean;
  job_id?: string;
  sanitized_dataset_id?: string;
  sanitization?: {
    original_count: number;
    passed_count: number;
    dropped_count: number;
    total_fixes: number;
  };
}> => {
  return apiClient.post(`/fine-tunes/${fineTunedModelId}/resubmit`, opts || {});
};

export const reevaluateFineTune = (
  fineTunedModelId: string,
  regenerate_datasets = true
): Promise<{ ok: boolean; regenerated_datasets: boolean }> => {
  return apiClient.post(`/fine-tunes/${fineTunedModelId}/reevaluate`, {
    regenerate_datasets,
  });
};

export interface ExecutionTraceEvent {
  type: 'tool_call' | 'tool_response' | 'content' | 'final_response';
  tool?: string;
  arguments?: Record<string, unknown>;
  response?: unknown;
  content?: string;
  timestamp?: string;
}

export interface ExecutionTrace {
  events: ExecutionTraceEvent[];
  tools_used: string[];
  tool_calls_count: number;
  duration_ms: number;
}

export interface JudgeScore {
  overall: number;
  reasoning_quality: number;
  standards_compliance: number;
  engineering_depth: number;
  numerical_rigor: number;
  explanations?: {
    overall?: string;
    reasoning_quality?: string;
    standards_compliance?: string;
    engineering_depth?: string;
    numerical_rigor?: string;
  };
}

export interface ModelTrace {
  model_id: string;
  temperature: number | null;
  started_at: string | null;
  completed_at: string | null;
  duration_ms: number | null;
  error_message: string | null;
  trace: ExecutionTrace;
  judge_score: JudgeScore | null;
  final_answer: string;
}

export interface JudgeTrace extends ModelTrace {
  judgment: {
    winner: 'base' | 'ft' | 'tie';
    margin: number;
    reasoning: string;
  } | null;
  judged_trace_ids: string[];
}

export interface EvaluationQuestion {
  index: number;
  question_text: string | null;
  question_messages: unknown;
  base: ModelTrace | null;
  ft: ModelTrace | null;
  judge: JudgeTrace | null;
}

export interface EvaluationTraceResponse {
  evaluation_id: string | null;
  fine_tuned_model_id: string;
  base_model: string;
  ft_model: string;
  created_at: string | null;
  total_questions: number;
  aggregate_metrics: Record<string, unknown>;
  questions: EvaluationQuestion[];
}

export const getEvaluationTraces = (
  fineTunedModelId: string,
  evaluationId?: string
): Promise<EvaluationTraceResponse> => {
  const params = evaluationId ? `?evaluation_id=${evaluationId}` : '';
  return apiClient.get(
    `/evaluation-traces/fine-tune/${fineTunedModelId}${params}`
  );
};

export const listCompanyProjects = (
  companyId: string
): Promise<{ items: Array<{ id: string; name: string }> }> => {
  return apiClient.get(`/companies/${companyId}/projects`);
};

export const uploadCompanyArtifacts = async (
  companyId: string,
  projectId: string | undefined,
  files: File[]
): Promise<{ uploaded: string[] }> => {
  // Compatibility alias; routes to first-class company documents endpoint.
  return uploadCompanyDocuments(companyId, files, projectId);
};

// Profile types and functions
export interface Profile {
  id: string;
  email: string;
  first_name?: string;
  last_name?: string;
  full_name: string;
  company_id?: string | null;
  is_system_admin?: boolean;
  is_user_admin?: boolean;
  timezone?: string;
}

export interface ProfileUpdateRequest {
  first_name?: string;
  last_name?: string;
  full_name?: string;
  email?: string;
  timezone?: string;
}

export const getProfile = (): Promise<Profile> => {
  return apiClient.get<Profile>('/profile/');
};

export interface PlatformMe {
  is_platform_admin: boolean;
}

export const getPlatformMe = (): Promise<PlatformMe> => {
  return apiClient.get<PlatformMe>('/platform/me');
};

export interface CustomPortalSection {
  id: string;
  name: string;
  tile_ids: string[];
}

export interface PortalPreferences {
  sidebar_collapsed: boolean;
  section_collapsed: Record<string, boolean>;
  favorite_tile_ids: string[];
  /** After any favorite edit; when false with empty ids, client shows all catalog apps as pinned. */
  favorite_sets_explicit?: boolean;
  recents: Array<{ tile_id: string; opened_at: string }>;
  custom_portal_sections?: CustomPortalSection[];
  /** Order of portal app sections (built-in keys + `custom:<id>`). */
  portal_section_order?: string[];
  /** Section keys hidden on the main portal column (still in sidebar). */
  portal_section_hidden?: string[];
  /** Preferred order of All apps tiles (ids); missing tiles append alphabetically. */
  all_apps_tile_order?: string[];
}

export const getPortalPreferences = (): Promise<PortalPreferences> => {
  return apiClient.get<PortalPreferences>('/profile/portal');
};

export const patchPortalPreferences = (body: {
  sidebar_collapsed?: boolean;
  section_collapsed?: Record<string, boolean>;
  favorite_tile_ids?: string[];
  custom_portal_sections?: CustomPortalSection[];
  portal_section_order?: string[];
  portal_section_hidden?: string[];
  all_apps_tile_order?: string[];
}): Promise<PortalPreferences> => {
  return apiClient.patch<PortalPreferences>('/profile/portal', body);
};

export const postPortalRecent = (
  tileId: string
): Promise<PortalPreferences> => {
  return apiClient.post<PortalPreferences>('/profile/portal/recents', {
    tile_id: tileId,
  });
};

export interface TenantBranding {
  slug: string;
  display_name: string | null;
}

export const getTenantBranding = (): Promise<TenantBranding> => {
  return apiClient.get<TenantBranding>('/platform/tenant-branding');
};

export interface PlatformTenantRow {
  id: string;
  slug: string;
  display_name: string;
  status: string;
  database_url_secret_ref: string | null;
  has_tenant_database_url: boolean;
  /** Neon project id (for branching / ephemeral debug environments). */
  neon_project_id?: string | null;
  deleted_at?: string | null;
  can_delete?: boolean;
  delete_block_reason?: string | null;
}

export const listPlatformTenants = (): Promise<PlatformTenantRow[]> => {
  return apiClient.get<PlatformTenantRow[]>('/platform/tenants');
};

export const createPlatformTenant = (payload: {
  slug: string;
  display_name: string;
  status?: string;
  tenant_database_url?: string | null;
  database_url_secret_ref?: string | null;
  /** Default true: create Neon project + run tenant migrations when no manual DSN. */
  provision_neon?: boolean;
}): Promise<PlatformTenantRow> => {
  return apiClient.post<PlatformTenantRow>('/platform/tenants', payload);
};

export const bootstrapProtioTenant = (): Promise<PlatformTenantRow> => {
  return apiClient.post<PlatformTenantRow>(
    '/platform/tenants/bootstrap-protio',
    {}
  );
};

export const deletePlatformTenant = (
  tenantId: string,
  confirmationSlug: string
): Promise<PlatformTenantRow> => {
  return apiClient.post<PlatformTenantRow>(
    `/platform/tenants/${tenantId}/delete`,
    { confirmation_slug: confirmationSlug }
  );
};

export const patchTenantNeonProjectId = (
  tenantId: string,
  neon_project_id: string
): Promise<PlatformTenantRow> => {
  return apiClient.patch<PlatformTenantRow>(
    `/platform/tenants/${tenantId}/neon-project`,
    { neon_project_id }
  );
};

export interface EphemeralEnvironmentSummary {
  id: string;
  name: string;
  status: string;
  created_at: string;
  updated_at: string;
  expires_at: string | null;
}

export interface EphemeralEnvironmentTenantItem {
  id: string;
  source_tenant_id: string;
  source_tenant_slug: string;
  source_neon_project_id: string;
  parent_branch_id: string;
  branched_branch_id?: string | null;
  branched_pooled_url?: string | null;
  copy_status: string;
  masking_status?: string | null;
}

export interface EphemeralEnvironmentDetail {
  id: string;
  name: string;
  status: string;
  created_by_email: string;
  parent_control_plane_branch_id: string;
  control_plane_neon_project_id: string;
  branched_control_plane_branch_id?: string | null;
  branched_cp_pooled_url?: string | null;
  data_mode: string;
  masking_profile?: string | null;
  notes?: string | null;
  expires_at?: string | null;
  side_effects_disabled: boolean;
  last_error?: string | null;
  created_at: string;
  updated_at: string;
  tenants: EphemeralEnvironmentTenantItem[];
  env_snippet?: string | null;
}

export const listEphemeralEnvironments = (): Promise<
  EphemeralEnvironmentSummary[]
> => {
  return apiClient.get<EphemeralEnvironmentSummary[]>(
    '/platform/ephemeral-environments'
  );
};

export const getEphemeralEnvironment = (
  id: string
): Promise<EphemeralEnvironmentDetail> => {
  return apiClient.get<EphemeralEnvironmentDetail>(
    `/platform/ephemeral-environments/${id}`
  );
};

export const createEphemeralEnvironment = (payload: {
  name: string;
  tenant_ids: string[];
  parent_control_plane_branch_id?: string | null;
  ttl_hours?: number | null;
  permanent?: boolean;
  data_mode?: string;
  masking_profile?: string | null;
  notes?: string | null;
  side_effects_disabled?: boolean | null;
}): Promise<EphemeralEnvironmentDetail> => {
  return apiClient.post<EphemeralEnvironmentDetail>(
    '/platform/ephemeral-environments',
    payload
  );
};

export const destroyEphemeralEnvironment = (
  id: string
): Promise<EphemeralEnvironmentDetail> => {
  return apiClient.post<EphemeralEnvironmentDetail>(
    `/platform/ephemeral-environments/${id}/destroy`,
    {}
  );
};

// System Admin (is_user_admin) - user/org management
export interface AdminUserListItem {
  id: string;
  email: string;
  full_name: string;
  first_name?: string | null;
  last_name?: string | null;
  is_system_admin: boolean;
  is_user_admin: boolean;
  company_id?: string | null;
  company_name?: string | null;
  created_at: string;
}

// Admin Companies/Organizations (System Admin / is_user_admin only)
export interface AdminCompanyListItem {
  id: string;
  name: string;
  domain?: string | null;
  created_at: string;
}

export const listAdminCompanies = (): Promise<AdminCompanyListItem[]> => {
  return apiClient.get<AdminCompanyListItem[]>('/admin/companies');
};

export const createAdminCompany = (payload: {
  name: string;
  domain?: string | null;
}): Promise<AdminCompanyListItem> => {
  return apiClient.post<AdminCompanyListItem>('/admin/companies', payload);
};

export const listAdminUsers = (
  search?: string
): Promise<AdminUserListItem[]> => {
  const params = search ? `?search=${encodeURIComponent(search)}` : '';
  return apiClient.get<AdminUserListItem[]>(`/admin/users${params}`);
};

export const createAdminUser = (payload: {
  email: string;
  full_name: string;
  first_name?: string | null;
  last_name?: string | null;
  password?: string | null;
  company_id?: string | null;
}): Promise<AdminUserListItem> => {
  return apiClient.post<AdminUserListItem>('/admin/users', payload);
};

export const updateAdminUser = (
  userId: string,
  payload: {
    full_name?: string;
    first_name?: string | null;
    last_name?: string | null;
    is_user_admin?: boolean;
    is_system_admin?: boolean;
    company_id?: string | null;
  }
): Promise<AdminUserListItem> => {
  return apiClient.patch<AdminUserListItem>(`/admin/users/${userId}`, payload);
};

export const deleteAdminUser = (userId: string): Promise<void> => {
  return apiClient.delete<void>(`/admin/users/${userId}`);
};

export const listModelContextWindows = (): Promise<ModelContextWindow[]> => {
  return apiClient.get<ModelContextWindow[]>('/admin/model-context-windows/');
};

export const createModelContextWindow = (
  payload: Omit<ModelContextWindow, 'created_at'>
): Promise<ModelContextWindow> => {
  return apiClient.post<ModelContextWindow>(
    '/admin/model-context-windows/',
    payload
  );
};

export const updateModelContextWindow = (
  modelName: string,
  payload: Partial<Omit<ModelContextWindow, 'model_name' | 'created_at'>>
): Promise<ModelContextWindow> => {
  return apiClient.put<ModelContextWindow>(
    `/admin/model-context-windows/${modelName}`,
    payload
  );
};

export const deleteModelContextWindow = (modelName: string): Promise<void> => {
  return apiClient.delete<void>(`/admin/model-context-windows/${modelName}`);
};

// Standards Studio (stubs)
export type StandardsStatus =
  | 'PENDING'
  | 'IN_PROGRESS'
  | 'COMPLETED'
  | 'FAILED';
export type StandardType = 'REGULATORY' | 'COST_DATA';

// Regulatory standards have paraphrase status
export interface RegulatoryStandard {
  id: string;
  original: { filename: string; download_url: string };
  paraphrase?: { filename: string; download_url: string };
  status: StandardsStatus;
  created_at: string;
}

// Cost books have license information, no paraphrase
export interface CostBook {
  id: string;
  filename: string;
  download_url: string;
  created_at: string;
  status: StandardsStatus;
  license_holder?: string;
  license_expiration?: string;
  license_terms_url?: string;
}

export const listRegulatoryStandards = async (): Promise<{
  items: RegulatoryStandard[];
}> => {
  return apiClient.get<{ items: RegulatoryStandard[] }>(
    `/standards/regulatory`
  );
};

export const listCostBooks = async (): Promise<{ items: CostBook[] }> => {
  return apiClient.get<{ items: CostBook[] }>(`/standards/cost-books`);
};

export interface UploadStandardsOptions {
  files: File[];
  standardType?: string;
  licenseHolder?: string;
  licenseTermsUrl?: string;
  licenseExpiration?: string;
  /** Company ID (System Admin only) - defaults to user's organization */
  companyId?: string | null;
}

export const uploadStandards = async (
  options: UploadStandardsOptions
): Promise<{ uploaded: string[] }> => {
  const token = localStorage.getItem('authToken');
  const fd = new FormData();

  // Add files
  for (const f of options.files) {
    fd.append('files', f);
  }

  // Add metadata
  if (options.standardType) {
    fd.append('standard_type', options.standardType);
  }
  if (options.licenseHolder) {
    fd.append('license_holder', options.licenseHolder);
  }
  if (options.licenseTermsUrl) {
    fd.append('license_terms_url', options.licenseTermsUrl);
  }
  if (options.licenseExpiration) {
    fd.append('license_expiration', options.licenseExpiration);
  }
  if (options.companyId) {
    fd.append('company_id', options.companyId);
  }

  const res = await fetch(`${API_BASE_URL}/standards/upload`, {
    method: 'POST',
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: fd,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `Upload failed: ${res.status}`);
  }
  return res.json();
};

export const requestParaphrase = async (
  id: string,
  opts?: {
    temperature?: number;
    allow_ocr?: boolean;
    document_type?: string;
    use_vision?: boolean;
  }
): Promise<{ ok: boolean; job_id?: string }> => {
  return apiClient.post(`/standards/${id}/paraphrase`, opts || {});
};

export const updateProfile = (data: ProfileUpdateRequest): Promise<Profile> => {
  return apiClient.put<Profile>('/profile/', data);
};

// Password change functionality
export interface PasswordChangeRequest {
  current_password: string;
  new_password: string;
}

export interface PasswordChangeResponse {
  message: string;
}

export const changePassword = (
  data: PasswordChangeRequest
): Promise<PasswordChangeResponse> => {
  return apiClient.post<PasswordChangeResponse>('/auth/change-password', data);
};

// Input artifacts (RFP documents, etc.)
export interface InputArtifact {
  id: string;
  filename: string;
  type: string;
  metadata: unknown;
  created_at: string;
  file_size: number;
  content_type?: string;
}

export const getProjectInputArtifacts = (
  projectId: string
): Promise<InputArtifact[]> => {
  return apiClient.get<InputArtifact[]>(
    `/projects/${projectId}/input-artifacts`
  );
};

export const uploadProjectInputArtifacts = (
  projectId: string,
  files: File[],
  description?: string
): Promise<{ uploaded: Array<{ artifact_id: string; filename: string }> }> => {
  const formData = new FormData();
  for (const f of files) formData.append('files', f);
  if (description) formData.append('description', description);
  return apiClient.uploadFile(
    `/projects/${projectId}/input-artifacts/upload`,
    formData
  );
};

export const removeProjectInputArtifact = (
  projectId: string,
  artifactId: string
): Promise<{ ok: boolean; removed_artifact_id: string }> => {
  return apiClient.delete(
    `/projects/${projectId}/input-artifacts/${artifactId}`
  );
};

export const replaceProjectInputArtifact = (
  projectId: string,
  artifactId: string,
  file: File,
  description?: string
): Promise<{ ok: boolean; artifact_id: string; filename: string }> => {
  const formData = new FormData();
  formData.append('file', file);
  if (description) formData.append('description', description);
  return apiClient.uploadFile(
    `/projects/${projectId}/input-artifacts/${artifactId}/replace`,
    formData
  );
};

// Terms of Use API interfaces
export interface TermsAcceptanceStatus {
  accepted: boolean;
  user_authenticated: boolean;
  terms_id?: string;
  terms_version?: string;
  error?: string;
}

export interface TermsContent {
  id: string;
  content: string;
  version: string;
  created_at: string;
}

export const getTermsStatus = (): Promise<TermsAcceptanceStatus> => {
  return apiClient.get<TermsAcceptanceStatus>('/terms/status');
};

export const acceptTerms = async (): Promise<TermsAcceptanceStatus> => {
  // First get current terms to get the terms_id
  const termsStatus = await getTermsStatus();
  if (!termsStatus.terms_id) {
    throw new Error('No active terms found');
  }

  return apiClient.post<TermsAcceptanceStatus>('/terms/accept', {
    terms_id: termsStatus.terms_id,
  });
};

export const getTermsContent = (): Promise<TermsContent> => {
  return apiClient.get<TermsContent>('/terms/content');
};

// Deliverable flexibility APIs
export interface SectionType {
  id: string;
  key: string;
  title: string;
  description: string; // Legacy field
  definition?: string | null; // Core definition - what makes this analysis what it is
  methodology?: string | null; // How it's made: steps, processes, reference docs
  output_structure?:
    | 'TABULAR'
    | 'MARKDOWN'
    | 'STRUCTURED_JSON'
    | 'CAD'
    | 'PID'
    | 'MIXED'
    | 'CAD'
    | null; // Expected output format
  allowed_tools?: Record<string, unknown> | null;
  allowed_formats: string[];
  prompt_template?: string | null;
  schema?: Record<string, unknown> | null;
  rubric?: Record<string, unknown> | null;
  typical_content: string[];
  typical_content_text?: string | null;
  pitfalls?: string | null;
  mandatory_columns: string[];
  tabular_schemas?: { tab_name: string; mandatory_columns: string[] }[] | null;
  sample_artifact_id?: string | null;
  default_agent_id?: string | null;
  agent_instructions?: string | null;
  generated_agent_instructions?: string | null;
  style_guidelines?: Record<string, unknown> | null;
  contract_json?: Record<string, unknown> | null;
  contract_version: string;
  version: string;
  created_at: string;
  updated_at: string;
}

export interface TemplateSection {
  id: string;
  position: number;
  required: boolean;
  min_occurrences: number;
  max_occurrences: number;
  depends_on_keys: string[];
  variant_label?: string | null;
  extra_metadata?: Record<string, unknown> | null;
  dependency_type: 'required' | 'optional';
  output_format?: string | null;
  tabular_schemas?: Array<{
    tab_name: string;
    mandatory_columns: string[];
  }> | null;
  section_type: SectionType;
}

export interface DeliverableTemplate {
  id: string;
  name: string;
  description: string;
  allowed_formats: string[];
  allowed_tools?: Record<string, unknown> | null;
  version: string;
  is_active: boolean;
  sections: TemplateSection[];
  created_at: string;
  updated_at: string;
}

export interface SectionTypeInput {
  key: string;
  title: string;
  description?: string; // Legacy field, kept for backward compatibility
  definition?: string | null; // Core definition - what makes this analysis what it is
  methodology?: string | null; // How it's made: steps, processes, reference docs
  output_structure?:
    | 'TABULAR'
    | 'MARKDOWN'
    | 'STRUCTURED_JSON'
    | 'CAD'
    | 'PID'
    | 'MIXED'
    | 'CAD'
    | null; // Expected output format
  allowed_tools?: Record<string, unknown> | null;
  allowed_formats?: string[];
  prompt_template?: string | null;
  schema?: Record<string, unknown> | null;
  rubric?: Record<string, unknown> | null;
  typical_content?: string[];
  typical_content_text?: string | null;
  pitfalls?: string | null;
  mandatory_columns?: string[];
  tabular_schemas?: { tab_name: string; mandatory_columns: string[] }[] | null;
  sample_artifact_id?: string | null;
  default_agent_id?: string | null;
  agent_instructions?: string | null;
  generated_agent_instructions?: string | null;
  style_guidelines?: Record<string, unknown> | null;
  contract_json?: Record<string, unknown> | null;
  contract_version?: string;
  version?: string;
}

export interface TemplateSectionInput {
  section_key: string;
  position?: number;
  required?: boolean;
  min_occurrences?: number;
  max_occurrences?: number;
  depends_on_keys?: string[];
  variant_label?: string | null;
  extra_metadata?: Record<string, unknown> | null;
  dependency_type?: 'required' | 'optional';
  output_format?: string | null;
  tabular_schemas?: Array<{
    tab_name: string;
    mandatory_columns: string[];
  }> | null;
}

export type SectionSample = {
  id: string;
  filename: string;
  project_id: string;
  created_at: string;
  content_type?: string | null;
};

export const listSectionSamples = (
  companyId?: string
): Promise<{ items: SectionSample[] }> => {
  const query = companyId ? `?company_id=${companyId}` : '';
  return apiClient.get<{ items: SectionSample[] }>(
    `/deliverables/samples${query}`
  );
};

export const uploadSectionSample = (
  sectionKey: string,
  companyId: string,
  file: File
): Promise<{ artifact_id: string }> => {
  const form = new FormData();
  form.append('company_id', companyId);
  form.append('file', file);
  return apiClient.uploadFile<{ artifact_id: string }>(
    `/deliverables/sections/${sectionKey}/sample/upload`,
    form
  );
};

export const getMyCompany = (): Promise<{ id: string; name: string }> => {
  return apiClient.get<{ id: string; name: string }>(`/companies/me`);
};

export interface DeliverableTemplateInput {
  name: string;
  description: string;
  allowed_formats?: string[];
  allowed_tools?: Record<string, unknown> | null;
  version?: string;
  is_active?: boolean;
  sections?: TemplateSectionInput[];
}

export interface OrganizationPreset {
  id: string;
  company_id: string;
  template_id: string;
  enabled_section_keys: string[];
  ordering: string[];
  style_defaults?: Record<string, unknown> | null;
  extra_metadata?: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
}

export interface ProjectPlan {
  id: string;
  project_id: string;
  template_id?: string | null;
  selected_section_keys: string[];
  dependency_graph?: Record<string, unknown> | null;
  human_checkpoints?: Record<string, unknown> | null;
  extra_metadata?: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
}

export interface PlanDriftFieldDetail {
  field: string;
  stored?: unknown;
  canonical?: unknown;
}

export interface PlanDriftNodeDetail {
  section_key: string;
  title?: string | null;
  drifted: boolean;
  fields: PlanDriftFieldDetail[];
  section_type_updated_at?: string | null;
}

export interface PlanDriftReport {
  has_drift: boolean;
  nodes: PlanDriftNodeDetail[];
}

export const listSectionTypes = (): Promise<SectionType[]> => {
  return apiClient.get<SectionType[]>('/deliverables/sections');
};

export const createSectionType = (
  payload: SectionTypeInput
): Promise<SectionType> => {
  return apiClient.post<SectionType>('/deliverables/sections', payload);
};

export const updateSectionType = (
  sectionKey: string,
  payload: SectionTypeInput
): Promise<SectionType> => {
  return apiClient.put<SectionType>(
    `/deliverables/sections/${sectionKey}`,
    payload
  );
};

export const generateSectionInstructions = (
  sectionKey: string
): Promise<{ instructions: string }> => {
  return apiClient.post<{ instructions: string }>(
    `/deliverables/sections/${sectionKey}/generate-instructions`
  );
};

export const generateSectionInstructionsLLM = (
  sectionKey: string
): Promise<{ instructions: string }> => {
  return apiClient.post<{ instructions: string }>(
    `/deliverables/sections/${sectionKey}/generate-instructions-llm`
  );
};

export const generateSectionInstructionsFromData = (
  sectionData: SectionTypeInput
): Promise<{ instructions: string }> => {
  return apiClient.post<{ instructions: string }>(
    `/deliverables/sections/generate-instructions`,
    sectionData
  );
};

export const generateSectionInstructionsLLMFromData = (
  sectionData: SectionTypeInput
): Promise<{ instructions: string }> => {
  return apiClient.post<{ instructions: string }>(
    `/deliverables/sections/generate-instructions-llm`,
    sectionData
  );
};

export const listDeliverableTemplates = (
  includeSections = true
): Promise<DeliverableTemplate[]> => {
  const query = includeSections
    ? '?include_sections=true'
    : '?include_sections=false';
  return apiClient.get<DeliverableTemplate[]>(
    `/deliverables/templates${query}`
  );
};

export const createDeliverableTemplate = (
  payload: DeliverableTemplateInput
): Promise<DeliverableTemplate> => {
  return apiClient.post<DeliverableTemplate>(
    '/deliverables/templates',
    payload
  );
};

export const updateDeliverableTemplate = (
  templateId: string,
  payload: DeliverableTemplateInput
): Promise<DeliverableTemplate> => {
  return apiClient.put<DeliverableTemplate>(
    `/deliverables/templates/${templateId}`,
    payload
  );
};

export const listOrgPresets = (
  companyId?: string
): Promise<OrganizationPreset[]> => {
  const query = companyId ? `?company_id=${companyId}` : '';
  return apiClient.get<OrganizationPreset[]>(
    `/deliverables/org-presets${query}`
  );
};

export const upsertOrgPreset = (payload: {
  company_id: string;
  template_id: string;
  enabled_section_keys?: string[];
  ordering?: string[];
  style_defaults?: Record<string, unknown> | null;
  extra_metadata?: Record<string, unknown> | null;
}): Promise<OrganizationPreset> => {
  return apiClient.post<OrganizationPreset>(
    '/deliverables/org-presets',
    payload
  );
};

export const getProjectPlan = (projectId: string): Promise<ProjectPlan> => {
  return apiClient.get<ProjectPlan>(`/deliverables/projects/${projectId}/plan`);
};

export const getProjectPlanDrift = (
  projectId: string
): Promise<PlanDriftReport> => {
  return apiClient.get<PlanDriftReport>(
    `/deliverables/projects/${projectId}/plan/drift`
  );
};

export const syncProjectPlanFormats = (
  projectId: string,
  payload: { section_keys?: string[] | null }
): Promise<ProjectPlan> => {
  return apiClient.post<ProjectPlan>(
    `/deliverables/projects/${projectId}/plan/sync-formats`,
    payload
  );
};

export const saveProjectPlan = (
  projectId: string,
  payload: {
    template_id: string;
    selected_section_keys?: string[];
    sections?: TemplateSectionInput[];
    human_checkpoints?: Record<string, unknown> | null;
    extra_metadata?: Record<string, unknown> | null;
  }
): Promise<ProjectPlan> => {
  return apiClient.post<ProjectPlan>(
    `/deliverables/projects/${projectId}/plan`,
    payload
  );
};

export const listLatestDeliverableQualityRecords = (
  projectId: string,
  artifactIds: string[]
): Promise<DeliverableQualityRecord[]> => {
  const params = new URLSearchParams();
  artifactIds.forEach((id) => params.append('artifact_ids', id));
  const query = params.toString();
  return apiClient.get<DeliverableQualityRecord[]>(
    `/deliverable-quality/projects/${projectId}/records/latest${query ? `?${query}` : ''}`
  );
};

export const evaluateDeliverableQuality = (
  artifactId: string
): Promise<DeliverableQualityRecord> => {
  return apiClient.post<DeliverableQualityRecord>(
    `/deliverable-quality/artifacts/${artifactId}/evaluate`
  );
};

export const waiveDeliverableQualityItem = (
  recordId: string,
  payload: { checklist_item_id: string; rationale: string }
): Promise<DeliverableQualityRecord> => {
  return apiClient.post<DeliverableQualityRecord>(
    `/deliverable-quality/records/${recordId}/waivers`,
    payload
  );
};

export const createPortalDocumentDraft = (
  companyId: string
): Promise<{ company_document_id: string }> => {
  return apiClient.post<{ company_document_id: string }>(
    `/companies/${companyId}/documents/editor-draft`
  );
};

export const getDocumentSession = (
  artifactId: string
): Promise<DocumentSession> => {
  return apiClient.get<DocumentSession>(
    `/documents/artifacts/${artifactId}/session`
  );
};

export const getCompanyDocumentSession = (
  companyDocumentId: string
): Promise<DocumentSession> => {
  return apiClient.get<DocumentSession>(
    `/documents/company-documents/${companyDocumentId}/session`
  );
};

export const saveDocumentRevision = (
  artifactId: string,
  payload: DocumentSaveRequest
): Promise<DocumentSaveResponse> => {
  return apiClient.post<DocumentSaveResponse>(
    `/documents/artifacts/${artifactId}/save`,
    payload
  );
};

export const saveCompanyDocumentRevision = (
  companyDocumentId: string,
  payload: DocumentSaveRequest
): Promise<DocumentSaveResponse> => {
  return apiClient.post<DocumentSaveResponse>(
    `/documents/company-documents/${companyDocumentId}/save`,
    payload
  );
};

export const pushLiveDocumentState = (
  artifactId: string,
  documentData: DocumentEditorState,
  seq?: number
): Promise<void> => {
  return apiClient.put<void>(`/documents/artifacts/${artifactId}/live-state`, {
    document_data: documentData,
    live_state_seq: seq,
  });
};

export const pushLiveCompanyDocumentState = (
  companyDocumentId: string,
  documentData: DocumentEditorState,
  seq?: number
): Promise<void> => {
  return apiClient.put<void>(
    `/documents/company-documents/${companyDocumentId}/live-state`,
    {
      document_data: documentData,
      live_state_seq: seq,
    }
  );
};

export const getDocumentSourceBlob = (artifactId: string): Promise<Blob> => {
  return fetchBlobResponse(`/documents/artifacts/${artifactId}/source`);
};

export const getCompanyDocumentSourceBlob = (
  companyDocumentId: string
): Promise<Blob> => {
  return fetchBlobResponse(
    `/documents/company-documents/${companyDocumentId}/source`
  );
};

export const finalizeDeliverableQuality = (
  recordId: string
): Promise<DeliverableQualityRecord> => {
  return apiClient.post<DeliverableQualityRecord>(
    `/deliverable-quality/records/${recordId}/finalize`
  );
};

export default apiClient;

// Streaming helpers
export interface ActionTraceEvent {
  turn_id: string;
  event_index?: number;
  tool_name?: string;
  call_id?: string | null;
  action_type: string;
  phase?: string;
  display_text: string;
  detail_text?: string | null;
  status: 'in_progress' | 'completed' | 'failed';
  timestamp: string;
}

export type ChatStreamEvent = {
  type:
    | 'stage'
    | 'delta'
    | 'thinking_delta'
    | 'final'
    | 'session'
    | 'error'
    | 'user_message'
    | 'assistant_message'
    | 'session_updated'
    | 'action_trace'
    | 'spreadsheet_sync'
    | 'spreadsheet_patch'
    | 'spreadsheet_structure'
    | 'spreadsheet_fill'
    | 'workbook_plan_patch'
    | 'document_replace_content'
    | 'document_insert_content'
    | 'document_format_range'
    | 'document_pdf_annotation'
    | 'document_save_request'
    /** CAD tool completed; client should refresh jobs / design queries */
    | 'cad_workspace_refresh';
  turn_id?: string;
  event_index?: number;
  live_state_seq?: number;
  code?: string;
  text?: string;
  data?: Record<string, unknown>;
  timestamp?: string;
  /** Present only when type === 'spreadsheet_patch' */
  patches?: Array<{
    sheet: string;
    address: string;
    value?: number | string | boolean | null;
    formula?: string | null;
  }>;
  /** Present only when type === 'spreadsheet_structure' */
  ops?: Array<
    | {
        kind:
          | 'insert_row'
          | 'delete_row'
          | 'insert_col'
          | 'delete_col'
          | 'add_tab'
          | 'delete_tab'
          | 'create_chart'
          | 'delete_chart'
          | 'delete_table'
          | 'append_table_columns'
          | 'append_table_rows'
          | 'create_table'
          | 'move_table';
        sheet: string;
        table?: string;
        headers?: string[];
        ref?: string;
        style_source?: string;
        target_sheet?: string;
        target_start?: string;
        index?: number;
        count?: number;
        name?: string;
        mode?: 'keep_cells' | 'clear_contents';
        cascade_delete_charts?: string[];
        chart_type?: string;
        anchor_cell?: string;
        title?: string;
        source_table?: string;
        x_column?: string;
        y_columns?: string[];
      }
    | {
        kind: 'fill_down';
        sheet: string;
        source_range?: string;
        target_range?: string;
        mode?: 'formulas' | 'values' | 'formats' | 'all';
        preview_only?: boolean;
        max_cells?: number;
      }
    | {
        kind: 'add_table' | 'update_table' | 'remove_table';
        table_id?: string;
        table?: Record<string, unknown>;
      }
  >;
};

export const postNdjsonStream = async (
  endpoint: string,
  body: unknown,
  onEvent: (evt: ChatStreamEvent) => void
): Promise<void> => {
  const token = localStorage.getItem('authToken');
  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body ?? {}),
  });

  if (!response.ok || !response.body) {
    const errText = await response.text().catch(() => 'Stream error');
    throw new Error(errText || `HTTP error ${response.status}`);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  const deliverLine = (line: string) => {
    let evt: ChatStreamEvent;
    try {
      evt = JSON.parse(line) as ChatStreamEvent;
    } catch {
      return;
    }
    onEvent(evt);
    if (evt.type === 'error') {
      throw new Error(evt.text || 'Chat response interrupted');
    }
  };

  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let idx: number;
    while ((idx = buffer.indexOf('\n')) >= 0) {
      const line = buffer.slice(0, idx).trim();
      buffer = buffer.slice(idx + 1);
      if (!line) continue;
      deliverLine(line);
    }
  }
  if (buffer.trim()) {
    deliverLine(buffer.trim());
  }
};

// Chat sessions API
export type ChatSessionSummary = {
  id: string;
  title?: string | null;
  primary_project_id: string | null;
  reference_project_ids: string[];
  created_at: string;
  updated_at: string;
  message_count: number;
};

export type ChatSessionDetail = {
  id: string;
  title?: string | null;
  primary_project_id: string | null;
  reference_project_ids: string[];
  created_at: string;
  updated_at: string;
  fine_tuned_model_id?: string | null;
  effective_model_id?: string | null;
};

export const listChatSessions = (): Promise<ChatSessionSummary[]> => {
  return apiClient.get<ChatSessionSummary[]>(`/chat/sessions/`);
};

export const createChatSession = (payload: {
  primary_project_id?: string;
  reference_project_ids?: string[];
  title?: string;
  fine_tuned_model_id?: string;
  effective_model_id?: string;
  /** Spreadsheet-mode sessions are ephemeral and excluded from sidebar history. */
  is_ephemeral?: boolean;
}): Promise<ChatSessionDetail> => {
  return apiClient.post<ChatSessionDetail>(`/chat/sessions/`, payload);
};

export const getChatSessionDetail = (
  sessionId: string
): Promise<ChatSessionDetail> => {
  return apiClient.get<ChatSessionDetail>(`/chat/sessions/${sessionId}`);
};

export const patchChatSession = (
  sessionId: string,
  payload: { add_reference_project_ids?: string[]; title?: string }
): Promise<ChatSessionDetail> => {
  return apiClient.put<ChatSessionDetail>(
    `/chat/sessions/${sessionId}`,
    payload
  );
};

export const streamSessionMessage = async (
  sessionId: string,
  body: { message: string; context?: unknown },
  onEvent: (evt: ChatStreamEvent) => void
): Promise<void> => {
  return postNdjsonStream(
    `/chat/sessions/${sessionId}/message/stream`,
    body,
    onEvent
  );
};

// Model selection and task regeneration
export interface CompanyModel {
  id: string;
  display_name: string;
  is_fine_tuned: boolean;
  is_default: boolean;
  openai_model_id?: string;
  provider?: string;
  can_fine_tune?: boolean;
}

export const listCompanyModels = (
  companyId: string
): Promise<{ models: CompanyModel[] }> => {
  return apiClient.get<{ models: CompanyModel[] }>(
    `/companies/${companyId}/models`
  );
};

export const getCompanyDefaultModel = (
  companyId: string
): Promise<{ default_conversational_model: string | null }> => {
  return apiClient.get<{ default_conversational_model: string | null }>(
    `/companies/${companyId}/default-model`
  );
};

export const setCompanyDefaultModel = (
  companyId: string,
  defaultModel: string | null
): Promise<{ ok: boolean }> => {
  return apiClient.post<{ ok: boolean }>(
    `/companies/${companyId}/default-model`,
    {
      default_conversational_model: defaultModel,
    }
  );
};

export const regenerateTask = (
  projectId: string,
  payload: {
    task_name: string;
    model_id: string;
    custom_instructions?: string;
  }
): Promise<{
  task_id: string;
  status: string;
  dependencies_count: number;
  message: string;
}> => {
  return apiClient.post(`/projects/${projectId}/tasks/regenerate`, payload);
};

export interface RetryDeliverablesTaskPayload {
  task_name: string;
  model_id: string;
  custom_instructions?: string;
  include_downstream?: boolean;
}

export interface RetryDeliverablesTaskResultTask {
  task_id: string;
  section_key: string;
  status: string;
}

export const retryDeliverablesTask = (
  projectId: string,
  payload: RetryDeliverablesTaskPayload
): Promise<{
  tasks: RetryDeliverablesTaskResultTask[];
  count: number;
  message: string;
}> => {
  return apiClient.post(`/projects/${projectId}/tasks/retry`, {
    ...payload,
    include_downstream:
      payload.include_downstream !== undefined
        ? payload.include_downstream
        : true,
  });
};

// ============================================
// Artifact Downloads
// ============================================

export interface ArtifactDownloadResult {
  blob: Blob;
  filename?: string;
}

export interface SpreadsheetCellData {
  row?: number;
  col?: number;
  value?: number | string | boolean | null;
  formula?: string | null;
  error?: string | null;
  format?: string | null;
}

export interface SpreadsheetWorkbookData {
  sheets: Record<
    string,
    {
      cells: Record<string, SpreadsheetCellData>;
      tables?: SpreadsheetTableData[];
    }
  >;
  charts?: SpreadsheetChartData[];
  pivots?: SpreadsheetPivotDefinition[];
}

export interface SpreadsheetTableData {
  name: string;
  ref: string;
  columns?: string[];
  totalsRow?: boolean;
}

/** A durable, table-backed Excel chart in the browser spreadsheet session. */
export interface SpreadsheetChartData {
  name: string;
  sheet: string;
  chart_type: 'column' | 'bar' | 'line' | 'area' | 'pie' | string;
  anchor_cell: string;
  title?: string | null;
  source_table?: string | null;
  x_column?: string | null;
  y_columns?: string[];
  series?: Array<{ values?: string | null; categories?: string | null }>;
}

export interface SpreadsheetSessionResponse {
  artifact_id: string;
  artifact_type: string;
  filename: string;
  workbook_data: SpreadsheetWorkbookData;
  summary: {
    sheet_names: string[];
    sheet_count: number;
    total_cells: number;
    formula_cells: number;
    functions: string[];
  };
}

export interface SpreadsheetSaveResponse {
  artifact_id: string;
  revision_id: string;
  source_artifact_id: string;
  actor_type: string;
  summary: Record<string, unknown>;
}

export interface SpreadsheetPatchResponse {
  success: boolean;
  touched_cells: number;
  touched_sheets: string[];
  applied_ops: number;
  errors: string[];
  warnings: string[];
  workbook_data?: SpreadsheetWorkbookData;
  artifact_id?: string;
  revision_id?: string;
}

export interface SpreadsheetAgentEditResponse {
  success: boolean;
  patch: {
    schema_version: string;
    requests: Array<Record<string, unknown>>;
  };
  trace_events?: ActionTraceEvent[];
  touched_cells: number;
  touched_sheets: string[];
  applied_ops: number;
  errors: string[];
  warnings: string[];
  workbook_data?: SpreadsheetWorkbookData;
  artifact_id?: string;
  revision_id?: string;
}

/**
 * Download an artifact as a Blob with optional filename from Content-Disposition
 */
export const downloadArtifactBlob = async (
  artifactId: string
): Promise<ArtifactDownloadResult> => {
  const token = localStorage.getItem('authToken');
  const response = await fetch(
    `${API_BASE_URL}/artifacts/${artifactId}/download`,
    {
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    }
  );

  if (!response.ok) {
    throw new Error(`Download failed: ${response.status}`);
  }

  const blob = await response.blob();
  const contentDisposition = response.headers.get('Content-Disposition');
  const filename = contentDisposition
    ? contentDisposition.split('filename=')[1]?.replace(/"/g, '')
    : undefined;

  return { blob, filename };
};

/**
 * Download and save an artifact to the user's device
 */
export const downloadAndSaveArtifact = async (
  artifactId: string,
  overrideFilename?: string
): Promise<void> => {
  const { blob, filename } = await downloadArtifactBlob(artifactId);
  saveBlobToDevice(
    blob,
    overrideFilename || filename || `artifact_${artifactId}`
  );
};

export const downloadAndSaveCompanySpreadsheet = async (
  companyDocumentId: string,
  filename?: string
): Promise<void> => {
  const blob = await getCompanyDocumentSourceBlob(companyDocumentId);
  const requestedName =
    filename?.trim() || `spreadsheet_${companyDocumentId}.xlsx`;
  const xlsxName = /\.xlsx?$/i.test(requestedName)
    ? requestedName
    : `${requestedName}.xlsx`;
  saveBlobToDevice(blob, xlsxName);
};

const saveBlobToDevice = (blob: Blob, filename: string): void => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();

  window.URL.revokeObjectURL(url);
  document.body.removeChild(link);
};

export const getSpreadsheetSession = (
  artifactId: string
): Promise<SpreadsheetSessionResponse> => {
  return apiClient.get<SpreadsheetSessionResponse>(
    `/spreadsheets/artifacts/${artifactId}/session`
  );
};

export const createPortalSpreadsheetDraft = (
  companyId: string
): Promise<{ company_document_id: string }> => {
  return apiClient.post<{ company_document_id: string }>(
    `/companies/${companyId}/documents/spreadsheet-draft`,
    {}
  );
};

export const getCompanySpreadsheetSession = (
  companyDocumentId: string
): Promise<SpreadsheetSessionResponse> => {
  return apiClient.get<SpreadsheetSessionResponse>(
    `/spreadsheets/company-documents/${companyDocumentId}/session`
  );
};

export const saveSpreadsheetRevision = (
  artifactId: string,
  payload: {
    workbook_data: SpreadsheetWorkbookData;
    actor_type?: 'human' | 'agent';
    summary?: Record<string, unknown>;
    filename?: string;
  }
): Promise<SpreadsheetSaveResponse> => {
  return apiClient.post<SpreadsheetSaveResponse>(
    `/spreadsheets/artifacts/${artifactId}/save`,
    payload
  );
};

export const saveCompanySpreadsheetRevision = (
  companyDocumentId: string,
  payload: {
    workbook_data: SpreadsheetWorkbookData;
    actor_type?: 'human' | 'agent';
    summary?: Record<string, unknown>;
    filename: string;
  }
): Promise<SpreadsheetSaveResponse> => {
  return apiClient.post<SpreadsheetSaveResponse>(
    `/spreadsheets/company-documents/${companyDocumentId}/save`,
    payload
  );
};

/**
 * Push the current live calculated workbook state to the backend so that
 * inspect_excel_artifact reads the authoritative UI state instead of the
 * stale saved artifact. Callers handle retries and surface synchronization failures.
 */
export const pushLiveSpreadsheetState = (
  artifactId: string,
  workbookData: SpreadsheetWorkbookData,
  liveStateSeq?: number,
  operationError?: string,
  synchronization?: { editor_id: string; snapshot_version: number }
): Promise<void> => {
  return apiClient
    .put<void>(`/spreadsheets/artifacts/${artifactId}/live-state`, {
      workbook_data: workbookData,
      ...synchronization,
      ...(operationError
        ? { operation_error: operationError.slice(0, 1000) }
        : {}),
      ...(typeof liveStateSeq === 'number'
        ? { live_state_seq: liveStateSeq }
        : {}),
    })
    .then(() => undefined);
};

export const pushLiveCompanySpreadsheetState = (
  companyDocumentId: string,
  workbookData: SpreadsheetWorkbookData,
  liveStateSeq?: number,
  operationError?: string,
  synchronization?: { editor_id: string; snapshot_version: number }
): Promise<void> => {
  return apiClient
    .put<void>(
      `/spreadsheets/company-documents/${companyDocumentId}/live-state`,
      {
        workbook_data: workbookData,
        ...synchronization,
        ...(operationError
          ? { operation_error: operationError.slice(0, 1000) }
          : {}),
        ...(typeof liveStateSeq === 'number'
          ? { live_state_seq: liveStateSeq }
          : {}),
      }
    )
    .then(() => undefined);
};

export const applySpreadsheetPatch = (
  artifactId: string,
  payload: {
    schema_version: string;
    requests: Array<Record<string, unknown>>;
    workbook_data?: SpreadsheetWorkbookData;
    dry_run?: boolean;
    create_version?: boolean;
    actor_type?: 'human' | 'agent';
    summary?: Record<string, unknown>;
    filename?: string;
  }
): Promise<SpreadsheetPatchResponse> => {
  return apiClient.post<SpreadsheetPatchResponse>(
    `/spreadsheets/artifacts/${artifactId}/patch`,
    payload
  );
};

export const applySpreadsheetAgentEdit = (
  artifactId: string,
  payload: {
    prompt: string;
    workbook_data: SpreadsheetWorkbookData;
    selected_sheet?: string;
    selected_cell?: string;
    selected_range?: string;
    visible_range?: string;
    selected_model_id?: string;
    reference_project_ids?: string[];
    create_version?: boolean;
    filename?: string;
  }
): Promise<SpreadsheetAgentEditResponse> => {
  return apiClient.post<SpreadsheetAgentEditResponse>(
    `/spreadsheets/artifacts/${artifactId}/agent-edit`,
    payload
  );
};

export interface SpreadsheetLatestVersionResponse {
  artifact_id: string;
  filename: string;
  changed: boolean;
}

/**
 * Return the most recently created artifact descended from artifactId.
 * Call after an agent turn to detect whether the agent created a new version.
 */
export const getLatestSpreadsheetVersion = (
  artifactId: string
): Promise<SpreadsheetLatestVersionResponse> => {
  return apiClient.get<SpreadsheetLatestVersionResponse>(
    `/spreadsheets/artifacts/${artifactId}/latest-version`
  );
};

// ============================================
// P&ID designer APIs
// ============================================

export interface PidBootstrapResponse {
  project_id: string;
  diagram_id: string;
  name: string;
}

export const createBlankPidDiagram = (
  projectId: string,
  name = 'New P&ID Diagram'
): Promise<PidBootstrapResponse> => {
  return apiClient.post<PidBootstrapResponse>(
    `/projects/${projectId}/pid-diagrams`,
    { name }
  );
};

export const createPidDiagramFromArtifact = (
  projectId: string,
  artifactId: string
): Promise<PidBootstrapResponse> => {
  return apiClient.post<PidBootstrapResponse>(
    `/projects/${projectId}/pid-diagrams/from-artifact/${artifactId}`,
    {}
  );
};

export const getPidDiagram = (
  projectId: string,
  diagramId: string
): Promise<PidDiagram> => {
  return apiClient.get<PidDiagram>(
    `/projects/${projectId}/pid-diagrams/${diagramId}`
  );
};

export const createPidDiagram = (
  projectId: string,
  payload: {
    name: string;
    definition?: PidDiagramDefinition;
  }
): Promise<PidBootstrapResponse> => {
  return apiClient.post<PidBootstrapResponse>(
    `/projects/${projectId}/pid-diagrams`,
    payload
  );
};

export const savePidDiagramRevision = (
  projectId: string,
  diagramId: string,
  body: PidSaveRequest
): Promise<PidSaveResponse> => {
  return apiClient.post<PidSaveResponse>(
    `/projects/${projectId}/pid-diagrams/${diagramId}/revisions`,
    body
  );
};

export const validatePidDiagram = (
  projectId: string,
  diagramId: string,
  definition: PidDiagramDefinition
): Promise<PidValidationResponse> => {
  return apiClient.post<PidValidationResponse>(
    `/projects/${projectId}/pid-diagrams/${diagramId}/validate`,
    {
      definition,
    }
  );
};

export const exportPidDiagram = (
  projectId: string,
  diagramId: string,
  format: 'svg' | 'json' | 'csv'
): Promise<Blob> => {
  const params = new URLSearchParams({ format });
  return apiClient.downloadBlob(
    `/projects/${projectId}/pid-diagrams/${diagramId}/export?${params.toString()}`
  );
};

// ============================================
// Single-line diagram APIs
// ============================================

export interface SingleLineBootstrapResponse {
  project_id: string;
  diagram_id: string;
  name: string;
}

export const createSingleLineDiagram = (
  projectId: string,
  payload: {
    name: string;
    definition?: SingleLineDiagramDefinition;
  }
): Promise<SingleLineBootstrapResponse> => {
  return apiClient.post<SingleLineBootstrapResponse>(
    `/projects/${projectId}/single-line-diagrams`,
    payload
  );
};

export const getSingleLineDiagram = (
  projectId: string,
  diagramId: string
): Promise<SingleLineDiagram> => {
  return apiClient.get<SingleLineDiagram>(
    `/projects/${projectId}/single-line-diagrams/${diagramId}`
  );
};

export const listSingleLineDiagrams = (
  projectId: string
): Promise<SingleLineDiagramSummary[]> => {
  return apiClient.get<SingleLineDiagramSummary[]>(
    `/projects/${projectId}/single-line-diagrams`
  );
};

export const listSingleLineDiagramRevisions = (
  projectId: string,
  diagramId: string,
  options: { limit?: number; beforeRevisionNumber?: number } = {}
): Promise<SingleLineRevisionPage> => {
  const search = new URLSearchParams({
    limit: String(options.limit ?? 25),
  });
  if (options.beforeRevisionNumber !== undefined) {
    search.set('before_revision_number', String(options.beforeRevisionNumber));
  }
  return apiClient.get<SingleLineRevisionPage>(
    `/projects/${projectId}/single-line-diagrams/${diagramId}/revisions?${search}`
  );
};

export const getSingleLineDiagramRevision = (
  projectId: string,
  diagramId: string,
  revisionNumber: number
): Promise<SingleLineRevision> => {
  return apiClient.get<SingleLineRevision>(
    `/projects/${projectId}/single-line-diagrams/${diagramId}/revisions/${revisionNumber}`
  );
};

export const saveSingleLineDiagramRevision = (
  projectId: string,
  diagramId: string,
  body: {
    definition: SingleLineDiagramDefinition;
    name?: string;
    expected_revision_number: number;
    actor_type?: 'human' | 'agent';
    summary?: string;
  }
): Promise<SingleLineSaveResponse> => {
  return apiClient.post<SingleLineSaveResponse>(
    `/projects/${projectId}/single-line-diagrams/${diagramId}/revisions`,
    body
  );
};

export const validateSingleLineDiagram = (
  projectId: string,
  diagramId: string,
  definition: SingleLineDiagramDefinition
): Promise<SingleLineValidationResponse> => {
  return apiClient.post<SingleLineValidationResponse>(
    `/projects/${projectId}/single-line-diagrams/${diagramId}/validate`,
    { definition }
  );
};

export const calculateSingleLineDiagram = (
  definition: SingleLineDiagramDefinition,
  projectId?: string,
  diagramId?: string
): Promise<SingleLineCalculationResponse> => {
  const endpoint =
    projectId && diagramId
      ? `/projects/${projectId}/single-line-diagrams/${diagramId}/calculate`
      : '/single-line/calculate';
  return apiClient.post<SingleLineCalculationResponse>(endpoint, {
    definition,
  });
};

export type SingleLineExportFormat = 'json' | 'csv';

export interface SingleLineExportDownload {
  blob: Blob;
  filename: string;
}

const exportFilename = (
  response: Response,
  fallbackName: string,
  format: SingleLineExportFormat
) => {
  const disposition = response.headers.get('Content-Disposition') ?? '';
  const match = disposition.match(/filename="([^"]+)"/i);
  return match?.[1] ?? `${fallbackName}.${format}`;
};

export const exportSingleLineDiagram = async (
  payload: {
    name: string;
    definition: SingleLineDiagramDefinition;
  },
  format: SingleLineExportFormat,
  projectId?: string,
  diagramId?: string
): Promise<SingleLineExportDownload> => {
  const params = new URLSearchParams({ format });
  const endpoint =
    projectId && diagramId
      ? `/projects/${projectId}/single-line-diagrams/${diagramId}/export`
      : '/single-line/export';
  const response = await apiClient.postStream(
    `${endpoint}?${params.toString()}`,
    payload
  );
  return {
    blob: await response.blob(),
    filename: exportFilename(response, 'single-line-diagram', format),
  };
};

// ============================================
// Project Chat APIs
// ============================================

export interface ProjectChatMessage {
  role: 'user' | 'assistant';
  content: string;
  timestamp?: string;
  turn_id?: string;
  trace_events?: ActionTraceEvent[];
}

export interface ProjectChatSession {
  session_id: string;
  messages: ProjectChatMessage[];
  message_count: number;
}

/**
 * Get or create a chat session for a project
 */
export const getProjectChatSession = (
  projectId: string,
  chatSessionId: string
): Promise<ProjectChatSession> => {
  return apiClient.get<ProjectChatSession>(
    `/projects/${projectId}/chat/session?chat_session_id=${encodeURIComponent(chatSessionId)}`
  );
};

/** Load persisted messages for a session (works without a primary project). */
export const getChatSessionMessages = (
  sessionId: string
): Promise<ProjectChatSession> => {
  return apiClient.get<ProjectChatSession>(
    `/chat/sessions/${sessionId}/messages`
  );
};

/**
 * Send a chat message (non-streaming)
 */
export const sendProjectChatMessage = (
  projectId: string,
  payload: {
    message: string;
    chat_session_id?: string;
    stream?: boolean;
    context?: unknown;
  }
): Promise<{ response: string; session_id: string }> => {
  return apiClient.post(`/projects/${projectId}/chat/message`, payload);
};

/**
 * Stream a chat message response (returns raw Response for NDJSON streaming)
 */
export const streamProjectChatMessage = async (
  projectId: string,
  payload: {
    message: string;
    chat_session_id?: string;
    context?: unknown;
  }
): Promise<Response> => {
  return apiClient.postStream(
    `/projects/${projectId}/chat/message/stream`,
    payload
  );
};

export const streamProjectChatMessageEvents = async (
  projectId: string,
  payload: {
    message: string;
    chat_session_id?: string;
    context?: unknown;
  },
  onEvent: (evt: ChatStreamEvent) => void
): Promise<void> => {
  return postNdjsonStream(
    `/projects/${projectId}/chat/message/stream`,
    payload,
    onEvent
  );
};

/**
 * Upload a file to the project chat
 */
export const uploadProjectChatFile = (
  projectId: string,
  file: File,
  description?: string
): Promise<{ artifact_id: string; filename: string }> => {
  const formData = new FormData();
  formData.append('file', file);
  if (description) {
    formData.append('description', description);
  }
  return apiClient.uploadFile(`/projects/${projectId}/chat/upload`, formData);
};

export interface RecommendedTask {
  name: string;
  description?: string;
  priority?: number;
}

/**
 * Create tasks from chat recommendations
 */
export const createProjectChatTasks = (
  projectId: string,
  tasks: RecommendedTask[]
): Promise<{ created: string[]; message: string }> => {
  return apiClient.post(`/projects/${projectId}/chat/create-tasks`, {
    recommended_tasks: tasks,
  });
};

// =====================
// FEEDBACK API
// =====================

export interface FeedbackRequest {
  feedback_type: 'thumbs_up' | 'thumbs_down';
  feedback_text?: string;
  corrected_text?: string;
  attributed_model_ids?: string[];
}

export interface FeedbackResponse {
  id: string;
  artifact_id: string;
  feedback_type: string;
  created_at: string;
}

export interface EditResponse {
  id: string;
  original_artifact_id: string;
  edited_artifact_id: string;
  edit_scope: string;
  diff_summary: {
    added_lines: number;
    removed_lines: number;
    changed_lines: number;
    change_ratio: number;
    scope: string;
    is_meaningful: boolean;
  };
  created_at: string;
}

export interface FeedbackHistoryResponse {
  artifact_id: string;
  feedback: Array<{
    id: string;
    feedback_type: string;
    feedback_text?: string;
    has_correction: boolean;
    attributed_model_ids: string[];
    created_at: string;
  }>;
  edits: Array<{
    id: string;
    edited_artifact_id: string;
    edit_scope: string;
    change_ratio: number;
    attributed_model_ids: string[];
    created_at: string;
  }>;
}

/**
 * Submit thumbs up/down feedback on an artifact
 */
export const submitArtifactFeedback = (
  artifactId: string,
  request: FeedbackRequest
): Promise<FeedbackResponse> => {
  return apiClient.post(`/artifacts/${artifactId}/feedback`, request);
};

/**
 * Upload an edited version of an artifact
 */
export const uploadArtifactEdit = async (
  artifactId: string,
  file: File,
  attributedModelIds?: string[]
): Promise<EditResponse> => {
  const token = localStorage.getItem('authToken');
  const formData = new FormData();
  formData.append('file', file);
  if (attributedModelIds?.length) {
    formData.append('attributed_model_ids', attributedModelIds.join(','));
  }

  const res = await fetch(`${API_BASE_URL}/artifacts/${artifactId}/edit`, {
    method: 'POST',
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: formData,
  });
  if (!res.ok) {
    const error = await res.json().catch(() => ({ detail: 'Upload failed' }));
    throw new Error(error.detail || 'Upload failed');
  }
  return res.json();
};

/**
 * Get feedback history for an artifact
 */
export const getArtifactFeedback = (
  artifactId: string
): Promise<FeedbackHistoryResponse> => {
  return apiClient.get(`/artifacts/${artifactId}/feedback`);
};

// =====================
// DPO TRAINING API
// =====================

export interface DPOStatsResponse {
  new_count: number;
  used_count: number;
  total_count: number;
  min_required: number;
  can_train: boolean;
}

export interface ModelVersion {
  id: string;
  version: number;
  display_name: string;
  training_method: 'sft' | 'dpo';
  parent_model_id: string | null;
  status: string;
  is_active: boolean;
  openai_model_id: string | null;
  created_at: string | null;
  eval_score: number | null;
}

export interface ModelVersionsResponse {
  model_id: string;
  root_id: string;
  versions: ModelVersion[];
}

export interface CreateDPORequest {
  company_id: string;
  parent_model_id: string;
  display_name?: string;
  billing_acknowledged: boolean;
}

export interface CreateDPOResponse {
  fine_tuned_model_id: string;
  parent_model_id: string;
  status: string;
  training_method: string;
  stats: {
    total_pairs: number;
    new_pairs: number;
    replay_pairs: number;
    deduplicated: number;
    final_count: number;
  };
}

/**
 * Get DPO training statistics for a model
 */
export const getDPOStats = (modelId: string): Promise<DPOStatsResponse> => {
  return apiClient.get(`/fine-tunes/${modelId}/dpo-stats`);
};

/**
 * Get model version lineage
 */
export const getModelVersions = (
  modelId: string
): Promise<ModelVersionsResponse> => {
  return apiClient.get(`/fine-tunes/${modelId}/versions`);
};

/**
 * Create a DPO fine-tuning job
 */
export const createDPOFineTune = (
  request: CreateDPORequest
): Promise<CreateDPOResponse> => {
  return apiClient.post('/fine-tunes/dpo', request);
};

/**
 * Re-run evaluation for a model (uses consistent golden eval set)
 */
export const rerunModelEvaluation = (
  modelId: string,
  regenerateDatasets?: boolean
): Promise<{ status: string; message: string }> => {
  return apiClient.post(`/fine-tunes/${modelId}/reevaluate`, {
    regenerate_datasets: regenerateDatasets ?? false,
  });
};

// =====================
// CAD / server-side worker jobs
// =====================

export interface CadJobRow {
  id: string;
  company_id: string;
  project_id: string;
  artifact_id: string | null;
  status: string;
  operation: string;
  idempotency_key: string | null;
  input_keys: Record<string, unknown> | null;
  output_keys: Record<string, unknown> | null;
  error_code: string | null;
  error_message: string | null;
  last_stderr_excerpt: string | null;
  driver_revision: string | null;
  script_object_key: string | null;
  source_channel: string;
  attempt: number;
  created_at: string;
  updated_at: string;
  started_at: string | null;
  completed_at: string | null;
  canceled_at: string | null;
  /** Server-authored one-line outcome (engineer copy). */
  result_summary?: string | null;
}

export const listCadJobs = (
  projectId: string,
  artifactId?: string | null
): Promise<CadJobRow[]> => {
  const q = new URLSearchParams({ project_id: projectId });
  if (artifactId) {
    q.set('artifact_id', artifactId);
  }
  return apiClient.get<CadJobRow[]>(`/cad/jobs?${q.toString()}`);
};

export const getCadJob = (jobId: string): Promise<CadJobRow> => {
  return apiClient.get<CadJobRow>(`/cad/jobs/${jobId}`);
};

export const createCadJob = (body: {
  project_id: string;
  artifact_id?: string | null;
  operation: string;
  script_python?: string | null;
  input_keys?: Record<string, unknown> | null;
  source_channel?: string | null;
}): Promise<CadJobRow> => {
  return apiClient.post<CadJobRow>('/cad/jobs', body);
};

export const cancelCadJob = (jobId: string): Promise<CadJobRow> => {
  return apiClient.post<CadJobRow>(`/cad/jobs/${jobId}/cancel`, {});
};

export const downloadCadMeshBlob = (jobId: string): Promise<Blob> => {
  return apiClient.downloadBlob(`/cad/jobs/${jobId}/mesh`);
};

export const downloadCadStepAp242Blob = (jobId: string): Promise<Blob> => {
  return apiClient.downloadBlob(`/cad/jobs/${jobId}/export/step`);
};

export const downloadCadMeshStlBlob = (jobId: string): Promise<Blob> => {
  return apiClient.downloadBlob(`/cad/jobs/${jobId}/export/stl`);
};

export const downloadCadMeshTopology = (jobId: string): Promise<unknown> => {
  return apiClient.get<unknown>(`/cad/jobs/${jobId}/mesh-topology`);
};

export const uploadCadViewerCapture = (
  sessionId: string,
  blob: Blob
): Promise<{ capture_id: string; relative_path: string }> => {
  const fd = new FormData();
  fd.append('file', blob, 'viewport.png');
  return apiClient.uploadFile(`/cad/sessions/${sessionId}/viewer-capture`, fd);
};

export interface CadDesignRow {
  id: string;
  company_id: string;
  project_id: string;
  artifact_id: string | null;
  name: string;
  design_domain: string;
  status: string;
  current_revision_number: number;
  current_definition: Record<string, unknown>;
  latest_validation_issues: Array<Record<string, unknown>>;
  tree_sync_worker_etag?: string | null;
  tree_sync_merged_etag?: string | null;
  created_by_user_id: string | null;
  updated_by_user_id: string | null;
  created_at: string;
  updated_at: string;
}

export type CadTreeSyncStatus = 'synced' | 'syncing' | 'stale';

export interface CadTreeSyncStatusRow {
  status: CadTreeSyncStatus;
  worker_etag: string | null;
  merged_etag: string | null;
}

export interface CadTreeSyncMergeRow {
  design: CadDesignRow;
  structured_structure_applied: boolean;
  source_job_id: string | null;
  note: string | null;
}

export interface CadDesignRevisionRow {
  id: string;
  design_id: string;
  revision_number: number;
  summary: string | null;
  definition_snapshot: Record<string, unknown>;
  validation_issues: Array<Record<string, unknown>>;
  created_by_user_id: string | null;
  created_at: string;
}

export interface CadSessionRow {
  id: string;
  design_id: string;
  user_id: string;
  user_label: string;
  discipline: string;
  selected_component_id: string | null;
  last_seen_at: string;
}

export const listCadDesigns = (projectId: string): Promise<CadDesignRow[]> => {
  const q = new URLSearchParams({ project_id: projectId });
  return apiClient.get<CadDesignRow[]>(`/cad/designs?${q.toString()}`);
};

export const createBlankCadArtifact = (
  projectId: string
): Promise<{ artifact_id: string; filename: string; file_type: 'CAD' }> => {
  return apiClient.post<{
    artifact_id: string;
    filename: string;
    file_type: 'CAD';
  }>(`/cad/projects/${projectId}/blank-artifact`);
};

export const persistCadArtifactCompanyDocument = (
  artifactId: string,
  body?: { link_project_id?: string; unlink_all_project_links?: boolean }
): Promise<{ company_document_id: string }> => {
  return apiClient.post<{ company_document_id: string }>(
    `/cad/artifacts/${artifactId}/company-document`,
    body ?? {}
  );
};

export const getCadDesign = (designId: string): Promise<CadDesignRow> => {
  return apiClient.get<CadDesignRow>(`/cad/designs/${designId}`);
};

export const getCadTreeSync = (
  designId: string
): Promise<CadTreeSyncStatusRow> => {
  return apiClient.get<CadTreeSyncStatusRow>(
    `/cad/designs/${designId}/tree-sync`
  );
};

export const mergeCadTreeSync = (
  designId: string
): Promise<CadTreeSyncMergeRow> => {
  return apiClient.post<CadTreeSyncMergeRow>(
    `/cad/designs/${designId}/tree-sync/merge`,
    {}
  );
};

export interface CadRefinementRunRow {
  id: string;
  cad_design_id: string;
  project_id: string;
  company_id: string;
  fidelity_target: string;
  max_iterations: number;
  iteration_count: number;
  status: string;
  stop_reason: string | null;
  last_phase_summary: string | null;
  user_visible_progress: string | null;
  final_summary: string | null;
  created_at: string;
  updated_at: string;
}

export interface CadRefinementActiveRow {
  run: CadRefinementRunRow | null;
}

export const getCadRefinementActive = (
  designId: string
): Promise<CadRefinementActiveRow> => {
  return apiClient.get<CadRefinementActiveRow>(
    `/cad/designs/${designId}/refinement/active`
  );
};

export const cancelCadRefinement = (
  runId: string
): Promise<CadRefinementRunRow> => {
  return apiClient.post<CadRefinementRunRow>(
    `/cad/refinement/${runId}/cancel`,
    {}
  );
};

export interface CadModelingPrefsRow {
  favorites: string[];
  recents: string[];
  workspace_lens: string;
  /** Workbench mode id (003); server default `part`. */
  active_cad_experience_id: CadWorkbenchModeId;
}

export const getCadModelingPrefs = (): Promise<CadModelingPrefsRow> => {
  return apiClient.get<CadModelingPrefsRow>('/cad/modeling-preferences');
};

export const putCadModelingPrefs = (body: {
  favorites?: string[];
  recents?: string[];
  workspace_lens?: string;
  active_cad_experience_id?: CadWorkbenchModeId;
}): Promise<CadModelingPrefsRow> => {
  return apiClient.put<CadModelingPrefsRow>('/cad/modeling-preferences', body);
};

export const postCadModelingRecent = (
  commandId: string
): Promise<CadModelingPrefsRow> => {
  return apiClient.post<CadModelingPrefsRow>(
    '/cad/modeling-preferences/recent',
    {
      command_id: commandId,
    }
  );
};

export const startCadRefinementRunApi = (
  designId: string,
  body: { fidelity_target: string; max_iterations?: number }
): Promise<CadRefinementRunRow> => {
  return apiClient.post<CadRefinementRunRow>(
    `/cad/designs/${designId}/refinement/start`,
    body
  );
};

export const createCadDesign = (body: {
  project_id: string;
  artifact_id?: string | null;
  name: string;
  design_domain?: string;
  definition?: Record<string, unknown>;
  validation_issues?: Array<Record<string, unknown>>;
}): Promise<CadDesignRow> => {
  return apiClient.post<CadDesignRow>('/cad/designs', body);
};

export const updateCadDesign = (
  designId: string,
  body: {
    name?: string;
    design_domain?: string;
    status?: string;
  }
): Promise<CadDesignRow> => {
  return apiClient.patch<CadDesignRow>(`/cad/designs/${designId}`, body);
};

export const listCadDesignRevisions = (
  designId: string
): Promise<CadDesignRevisionRow[]> => {
  return apiClient.get<CadDesignRevisionRow[]>(
    `/cad/designs/${designId}/revisions`
  );
};

export const createCadDesignRevision = (
  designId: string,
  body: {
    expected_revision_number?: number;
    summary?: string | null;
    definition_snapshot?: Record<string, unknown>;
    validation_issues?: Array<Record<string, unknown>>;
  }
): Promise<CadDesignRow> => {
  return apiClient.post<CadDesignRow>(
    `/cad/designs/${designId}/revisions`,
    body
  );
};

export const listCadDesignSessions = (
  designId: string
): Promise<CadSessionRow[]> => {
  return apiClient.get<CadSessionRow[]>(`/cad/designs/${designId}/sessions`);
};

export const startCadDesignSession = (
  designId: string,
  body: {
    discipline?: string;
    selected_component_id?: string | null;
  }
): Promise<CadSessionRow> => {
  return apiClient.post<CadSessionRow>(
    `/cad/designs/${designId}/sessions`,
    body
  );
};

export const heartbeatCadDesignSession = (
  designId: string,
  sessionId: string,
  body: {
    selected_component_id?: string | null;
  }
): Promise<CadSessionRow> => {
  return apiClient.post<CadSessionRow>(
    `/cad/designs/${designId}/sessions/${sessionId}/heartbeat`,
    body
  );
};

export type WorkbookReviewStatus =
  | 'DRAFT'
  | 'AWAITING_PLAN_REVIEW'
  | 'EXECUTING'
  | 'AWAITING_CHECKPOINT_REVIEW'
  | 'LIVE_PREVIEW'
  | 'REGENERATING'
  | 'AWAITING_REPLACEMENT_REVIEW'
  | 'COMPLETED';

export interface WorkbookReviewInboxItem {
  id: string;
  project_id: string;
  review_request_id: string;
  read_at: string | null;
  resolved_at: string | null;
  payload: { checkpoint_key?: string; plan_id?: string; kind?: string };
  created_at: string;
}

export const createWorkbookWorkPlan = (
  projectId: string,
  body: {
    task_id: string;
    plan_payload: Record<string, unknown>;
    reviewer_ids?: string[];
  }
) =>
  apiClient.post<{
    id: string;
    status: WorkbookReviewStatus;
    current_revision: number;
  }>(`/workbook-reviews/projects/${projectId}/plans`, body);

export interface WorkbookWorkPlanDetail {
  id: string;
  task_id: string;
  status: WorkbookReviewStatus;
  current_revision: number;
  reviewer_ids: string[];
  plan_payload: Record<string, unknown>;
  /** Most recent workbook artifact produced for this plan's task, if any. */
  workbook_artifact_id: string | null;
  /** Where the plan detail selected its workbook artifact from. */
  workbook_artifact_source?: string | null;
  /** Whether the selected artifact belongs to the active workbook generation. */
  workbook_artifact_is_active?: boolean;
  task_status: string | null;
  execution_model_id: string;
  company_id: string | null;
  /** Nodes already created in this plan run; checkpoint settings are immutable. */
  completed_build_node_ids: string[];
  last_successful_checkpoint: {
    checkpoint_key: string;
    completed_node_count: number;
  } | null;
  can_retry_from_last_checkpoint: boolean;
  checkpoint_states: Record<string, string>;
  open_review: {
    id: string;
    checkpoint_key: string;
    expected_revision: number;
  } | null;
  latest_review: {
    id: string;
    checkpoint_key: string;
    status: string;
    expected_revision: number;
  } | null;
  restart_options?: WorkbookRestartOption[];
}

/** Server-authoritative lifecycle return target. Do not infer availability from order. */
export interface WorkbookRestartOption {
  checkpoint_key: string;
  checkpoint_id: string;
  label: string;
  order?: number;
  reached: boolean;
  current: boolean;
  enabled: boolean;
  source_review_id?: string | null;
  resume_workbook_artifact_id?: string | null;
  saved_snapshot: boolean;
  rebuild_current: boolean;
  preserved_checkpoint_keys: string[];
  preserved_labels: string[];
  rebuilt_checkpoint_keys: string[];
  rebuilt_labels: string[];
  unavailable_reason?: string | null;
}

export interface WorkbookRestartOptionsResponse {
  plan_id?: string;
  task_id?: string;
  current_revision: number | null;
  expected_generation_id: string | null;
  options: WorkbookRestartOption[];
}

export interface WorkbookRestartResponse {
  status: string;
  next_action: 'queued' | 'plan_review' | 'checkpoint_review' | string;
  target_checkpoint_key: string;
  task_id: string;
  review_id?: string | null;
  lineage_id: string;
  generation_id: string;
  base_workbook_artifact_id?: string | null;
  preserved_completed_node_ids?: string[];
  rebuild_current?: boolean;
}

export const getWorkbookWorkPlan = (planId: string) =>
  apiClient.get<WorkbookWorkPlanDetail>(`/workbook-reviews/plans/${planId}`);

export const getWorkbookPlanRestartOptions = (planId: string) =>
  apiClient.get<WorkbookRestartOptionsResponse>(
    `/workbook-reviews/plans/${planId}/restart-options`
  );

export const getWorkbookTaskRestartOptions = (taskId: string) =>
  apiClient.get<WorkbookRestartOptionsResponse>(
    `/workbook-reviews/tasks/${taskId}/restart-options`
  );

export const restartWorkbookPlan = (
  planId: string,
  body: {
    expected_revision: number;
    expected_generation_id: string | null;
    target_checkpoint_key: string;
    notes: string;
    model_id?: string;
  }
) =>
  apiClient.post<WorkbookRestartResponse>(
    `/workbook-reviews/plans/${planId}/restart`,
    body
  );

export const restartWorkbookTask = (
  taskId: string,
  body: {
    expected_generation_id: string | null;
    target_checkpoint_key: string;
    notes: string;
    model_id?: string;
  }
) =>
  apiClient.post<WorkbookRestartResponse>(
    `/workbook-reviews/tasks/${taskId}/restart`,
    body
  );

export const listProjectWorkbookWorkPlans = (projectId: string) =>
  apiClient.get<{
    items: Array<{ id: string; task_id: string; status: WorkbookReviewStatus }>;
  }>(`/workbook-reviews/projects/${projectId}/plans`);

export const reviseWorkbookWorkPlan = (
  planId: string,
  body: { expected_revision: number; plan_payload: Record<string, unknown> }
) =>
  apiClient.patch<{
    id: string;
    status: WorkbookReviewStatus;
    current_revision: number;
  }>(`/workbook-reviews/plans/${planId}`, body);

export const requestWorkbookCheckpoint = (
  planId: string,
  body: {
    checkpoint_key: string;
    snapshot: Record<string, unknown>;
    reviewer_ids?: string[];
  }
) =>
  apiClient.post<{ id: string; status: string; expected_revision: number }>(
    `/workbook-reviews/plans/${planId}/reviews`,
    body
  );

export const approveWorkbookCheckpoint = (
  reviewId: string,
  expectedRevision: number
) =>
  apiClient.post<{
    id: string;
    status: string;
    next_action: 'queued' | 'completed';
    artifact_id: string | null;
    execution: WorkbookReviewExecution;
  }>(`/workbook-reviews/reviews/${reviewId}/approve`, {
    expected_revision: expectedRevision,
  });

export type WorkbookReviewExecutionState =
  | 'approved'
  | 'queued'
  | 'running'
  | 'completed'
  | 'failed'
  | 'cancelled'
  | 'waiting_for_review';

export interface WorkbookReviewExecution {
  review_id: string;
  task_id: string;
  state: WorkbookReviewExecutionState;
  review_status: string;
  task_status: string | null;
  accepted_at: string | null;
  updated_at: string | null;
  error_message: string | null;
}

export const getWorkbookReviewExecution = (reviewId: string) =>
  apiClient.get<WorkbookReviewExecution>(
    `/workbook-reviews/reviews/${reviewId}/execution`
  );

export const regenerateWorkbookFromPriorCheckpoint = (
  reviewId: string,
  body: { notes: string; model_id?: string; checkpoint_key?: string }
) =>
  apiClient.post<{
    status: 'QUEUED';
    task_id: string;
    prior_checkpoint_key: string | null;
    base_workbook_artifact_id?: string | null;
  }>(
    `/workbook-reviews/reviews/${reviewId}/regenerate-from-prior-checkpoint`,
    body
  );

export const retryWorkbookFromLastCheckpoint = (
  planId: string,
  body: { model_id?: string }
) =>
  apiClient.post<{
    status: 'QUEUED';
    task_id: string;
    checkpoint_key: string;
    base_workbook_artifact_id?: string | null;
  }>(`/workbook-reviews/plans/${planId}/retry-from-last-checkpoint`, body);

export const getWorkbookCheckpoint = (reviewId: string) =>
  apiClient.get<{
    id: string;
    project_id: string;
    task_id: string;
    work_plan_id: string;
    checkpoint_key: string;
    status: string;
    expected_revision: number;
    snapshot: Record<string, unknown>;
    candidates: Array<{
      id: string;
      mode: 'manual' | 'live_preview' | 'regeneration';
      status: 'DRAFT' | 'PROPOSED' | 'ACCEPTED' | 'REJECTED' | 'DISCARDED';
      scope: Record<string, unknown>;
      candidate_snapshot: Record<string, unknown> | null;
      impact_summary: Record<string, unknown> | null;
    }>;
    events: Array<{
      id: string;
      action: string;
      payload: Record<string, unknown>;
      created_at: string;
    }>;
  }>(`/workbook-reviews/reviews/${reviewId}`);

export const startWorkbookReviewCandidate = (
  reviewId: string,
  body: {
    mode: 'manual' | 'live_preview' | 'regeneration';
    scope?: Record<string, unknown>;
  }
) =>
  apiClient.post<{ id: string; status: string; mode: string }>(
    `/workbook-reviews/reviews/${reviewId}/candidates`,
    body
  );

export const proposeWorkbookReviewCandidate = (
  candidateId: string,
  body: {
    candidate_snapshot: Record<string, unknown>;
    impact_summary?: Record<string, unknown>;
  }
) =>
  apiClient.put<{ id: string; status: string }>(
    `/workbook-reviews/candidates/${candidateId}`,
    body
  );

export const generateWorkbookPlanAIPreview = (
  planId: string,
  body: { candidate_id: string; instruction: string }
) =>
  apiClient.post<{
    id: string;
    status: string;
    candidate_snapshot: Record<string, unknown>;
    assistant_message: string;
  }>(`/workbook-reviews/plans/${planId}/ai-preview`, body);

export const cancelWorkbookReviewCandidate = (candidateId: string) =>
  apiClient.post<{ id: string; status: string }>(
    `/workbook-reviews/candidates/${candidateId}/cancel`,
    {}
  );

export const decideWorkbookReviewCandidate = (
  candidateId: string,
  decision: 'accept' | 'reject' | 'discard',
  expectedRevision: number
) =>
  apiClient.post<{ id: string; status: string }>(
    `/workbook-reviews/candidates/${candidateId}/${decision}`,
    { expected_revision: expectedRevision }
  );

export const registerWorkbookManualEdit = (
  reviewId: string,
  body: {
    expected_revision: number;
    workbook_artifact_id: string;
    workbook_snapshot: Record<string, unknown>;
    impact_summary?: Record<string, unknown>;
  }
) =>
  apiClient.post<{ id: string; status: string; mode: string }>(
    `/workbook-reviews/reviews/${reviewId}/manual-edits`,
    body
  );

export const saveWorkbookManualRevision = (
  reviewId: string,
  body: {
    expected_revision: number;
    source_artifact_id: string;
    workbook_data: SpreadsheetWorkbookData;
    impact_summary?: Record<string, unknown>;
  }
) =>
  apiClient.post<{
    artifact_id: string;
    revision_id: string;
    source_artifact_id: string;
    candidate_id: string;
    candidate_status: string;
  }>(`/workbook-reviews/reviews/${reviewId}/manual-revisions`, body);

export const listWorkbookReviewInbox = (projectId?: string) =>
  apiClient.get<{ items: WorkbookReviewInboxItem[] }>(
    `/workbook-reviews/inbox${projectId ? `?project_id=${encodeURIComponent(projectId)}` : ''}`
  );

export const markWorkbookReviewInboxRead = (inboxId: string) =>
  apiClient.post<{ id: string; read_at: string }>(
    `/workbook-reviews/inbox/${inboxId}/read`,
    {}
  );

// Export the apiClient instance for direct access when needed
export { apiClient };
